import { FormControl, FormGroup } from '@angular/forms';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, inject, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges, Type, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { Ability, subject } from '@casl/ability';
import { forkJoin, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, take, takeUntil, switchMap } from 'rxjs/operators';
import { DrawingDownloadMode, markupLockCopy, ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { RecentSheetsService } from '../../../../../../../../shared/services/recent-sheets.service';
import { PDF_PAGE_RANGES, PdfRenderService } from '../../../../../../../../shared/services/pdf-render.service';
import { findTextBoxes, PdfTextItem, PdfViewportLike, TextBox } from '../drawing-text-find';
import type { PDFDocumentLoadingTask } from 'pdfjs-dist';
import { MarkupService } from '../../../../../../../../shared/services/markup.service';
import { Markup, MarkupReviewEvent } from '../../../../../../../../shared/models/markup';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  DrawingLinkedDay,
  DrawingLinkedRecord,
  DrawingPlacement,
  DrawingRevisionDetail,
  DrawingRevisionStatus,
  DrawingSheetDetail,
} from '../../../../../../../../shared/models/drawing-sheet';
import { MarkupEditorConfig } from '../../../../../../../../shared/components/markup-editor/markup-editor.types';
import { AuthService } from '../../../../../../../../shared/services/auth.service';
import { draftSheetLink, parseStationInput, sheetStationText, stationInputText } from '../../../../../../../../shared/helpers/design-context';
import { sheetPointFromClick } from '../../../../../../../../shared/helpers/sheet-point';
import { PINLESS_EMPTY_HERE } from '../../../../../../../../shared/helpers/link-removal';
import {
  accuracyRadius,
  offSheetBearing,
  OffSheet,
  offSheetText,
  Proj4Fn,
  projectToSheet,
  SheetGeo,
  SheetPosition,
} from '../../../../../../../../shared/helpers/sheet-geo';
import { PROJ4_LOADER } from '../../../../../../../../shared/helpers/proj4-loader';
import { isPlaceableType, SHEET_TYPES, SheetType } from '../../../../../../../../shared/helpers/sheet-types';
import { ProjectMapManifestService, PublishedMapLayer } from '../../../../../../../../shared/services/project-map-manifest.service';
import { ProjectMapSourceService } from '../../../../../../../../shared/services/project-map-source.service';
import { ProjectReferenceLayerService } from '../../../../../../../../shared/services/project-reference-layer.service';
import { confirmAndWithdraw } from '../drawing-withdraw';
import { ProjectsService } from '../../../../../../core/projects.service';
import { AddToType, DRAWING_ADD_TO_PANELS } from '../drawing-add-to';
import { SheetFocusService } from '../../../../../../../../shared/services/sheet-focus.service';
import { DrawingUploadDialogComponent, DrawingUploadDialogData } from '../drawing-upload-dialog/drawing-upload-dialog.component';
import { DrawingSheetEditPanelComponent } from '../drawing-sheet-edit-panel/drawing-sheet-edit-panel.component';
import { DrawingsAnalytics } from '../drawings-analytics';
import {
  anchorScrollDelta,
  buttonsReleased,
  capturePointer,
  clampZoom,
  containWidth,
  DRAG_THRESHOLD_PX,
  DragTracker,
  GestureReporter,
  isPinchWheel,
  isSafariGesture,
  pageFraction,
  pinchDistance,
  releasePointer,
  SafariPinch,
  stepZoom,
  wheelZoomFactor,
} from '../sheet-zoom-pan';
import { MultiSectionFilterSection } from '../../../filters/multi-section-filter/multi-section-filter.component';
import { MarkupSaveResult } from '../../../../../../../../shared/components/markup-editor/markup-editor.types';
import {
  AS_BUILT_ACTION_COPY,
  AS_BUILT_STATUS_COPY,
  AsBuiltReviewAction,
  authorName,
  canAuthorChange,
  deleteMarkMessage,
  isAsBuilt,
  laterRevisions,
  MarkBox,
  MarkHistoryEntry,
  markHistory,
  markupBBox,
  reviewActions,
} from '../drawing-as-built';
import { DRAWING_MAP_PANELS, DrawingMapPanels } from '../drawing-map-panels';
import {
  dayTitle,
  isOpenStatus,
  linkedRecordNoun,
  linkedRecordRoute,
  markRecordRoute,
  markRecordText,
  MARKER_CAP,
  RECORD_MARKS_CAP,
  SHEET_ZOOMS,
  ALL_RECORDS,
  filterLayersBy,
  isAllRecords,
  RECORD_TYPE_LABELS,
  RecordFilter,
  recordMatches,
} from '../drawing-markers';
import { CommonService } from '../../../../../../../../shared/services/common.service';

const ADD_TO: { type: AddToType; label: string; subject: string }[] = [
  { type: 'issue', label: 'Issue', subject: 'Issue' },
  { type: 'rfi', label: 'RFI', subject: 'RFI' },
  { type: 'punch', label: 'Punch item', subject: 'PunchlistItem' },
];

const PANEL_PAGE = 20;

/**
 * W54: the viewer's Filter (N) as kept in the URL. `null` / `[]` are the defaults: markup null = Shared, Private and
 * As-built all shown; records null = every record type; show = the toggles that are on (Mine only, All record markup).
 */
export interface SheetViewFilter {
  markup: string[] | null;
  records: string[] | null;
  show: string[];
}

export interface SheetDraft {
  number: string;
  title: string;
  discipline: string;
  /** '' for no type (read as other) */
  sheetType: SheetType | '';
  start: string;
  end: string;
}

/** W64: the form's fields and the names the saved event reports */
const SHEET_DRAFT_FIELDS: [keyof SheetDraft, string][] = [
  ['number', 'number'], ['title', 'title'], ['discipline', 'discipline'], ['sheetType', 'sheet_type'], ['start', 'station_start'], ['end', 'station_end'],
];

/** The map group placed sheets publish into (API T16), when the API has not named it yet */
const DRAWINGS_GROUP = 'Drawings';

/** What the heading offers for the map: see `mapState`. */
export type SheetMapState = 'place' | 'newer' | 'processing' | 'review' | 'placed' | 'failed';

const STATUS_COPY: Record<DrawingRevisionStatus, string> = {
  draft: 'Draft',
  issued: 'Current',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
};

/** W52: the revision status badge's variant (Figma 32911:76232): current info, superseded warning, the rest neutral */
const STATUS_BADGE: Record<DrawingRevisionStatus, string> = {
  draft: 'status-badge--neutral',
  issued: 'status-badge--info',
  superseded: 'status-badge--warning',
  withdrawn: 'status-badge--neutral',
};

/** W70: how long the asked-for point pulses */
const FOCUS_PULSE_MS = 1600;
/** W61: full screen's fit keeps this far from the frame's edges (px) */
const FULL_FIT_MARGIN = 24;
/** W61: the Details panel's width plus its gap; below this frame width it covers the whole frame (phones) */
const DETAILS_PANEL_WIDTH = 384;
const DETAILS_OVERLAPS_BELOW = 768;
/** The docked frame's padding and the window height the docked page leaves to the header and toolbars (the scss) */
const DOCKED_FRAME_PADDING = 16;
const DOCKED_HEIGHT_RESERVE = 260;

/**
 * One revision of a sheet. Read-only, it shows the server-rendered page image with every visible
 * redline row's transparent preview stacked on top, and never fetches the set PDF. Redline opens
 * the markup editor on exactly this page of the set PDF.
 */
/** Show on map: the header map's full-screen resize settles before the layer is fitted (the map's own transitions wait 500 ms) */
export const SHOW_ON_MAP_SETTLE_MS = 500;

@Component({
  selector: 'app-drawing-sheet-viewer',
  templateUrl: './drawing-sheet-viewer.component.html',
  styleUrls: ['./drawing-sheet-viewer.component.scss'],
  standalone: false,
})
export class DrawingSheetViewerComponent implements OnChanges, OnDestroy {
  /** The app's toast (Figma 11893:79195): CommonService.notification, no action button */
  private readonly notify = inject(CommonService);
  /** W48: PostHog events (ids, counts and enums only) */
  private readonly track = inject(DrawingsAnalytics);
  @Input() projectId: string;
  @Input() sheetUuid: string | null = null;
  @Input() revisionUuid: string | null = null;
  /** An as-built mark to show: its card opens once the page and its marks have loaded (the As-built tab's Open on sheet) */
  @Input() markUuid: string | null = null;
  @Input() canConfigure = false;
  /** ProjectDrawing publish: placing also needs it (with the map layer rights) */
  @Input() canPublish = false;
  @Output() back = new EventEmitter<void>();
  @Output() openRevision = new EventEmitter<string>();
  /** Compare this revision with the one it supersedes */
  @Output() compare = new EventEmitter<{ revision: string; against: string }>();
  /** Carry-forward review for this sheet */
  @Output() openCarry = new EventEmitter<string>();
  /** W65: Upload new revision made a set: its review opens */
  @Output() openSet = new EventEmitter<string>();

  revision: DrawingRevisionDetail | null = null;
  sheet: DrawingSheetDetail | null = null;
  editorConfig: MarkupEditorConfig | null = null;
  /** Every markup row on this page visible to the user: shared rows plus their own private rows */
  layers: Markup[] | null = null;
  /** W44 Find on sheet: the query, its boxes on the page (fractions) and the one in focus */
  findOpen = false;
  findQuery = '';
  findMatches: TextBox[] = [];
  findIndex = 0;
  findBusy = false;
  findError = '';
  private pageText: { uuid: string; items: PdfTextItem[]; viewport: PdfViewportLike } | null = null;
  /** The set PDF Find reads, owned here (W44 I3): destroyed when the file changes and with the viewer */
  private findDoc: { file: string; task: PDFDocumentLoadingTask } | null = null;
  /** The latest Find: an older one finishing late changes nothing */
  private findSeq = 0;
  private readonly recentSheets = inject(RecentSheetsService);
  private readonly pdfRender = inject(PdfRenderService);
  showShared = true;
  showPrivate = true;
  /** Every record's marks (tagged rows), up to RECORD_MARKS_CAP; otherwise only the selected record's */
  showAllMarks = false;
  /** As-built marks always render, under their own toggle (spec §15) */
  showAsBuilt = true;
  /** The as-built mark whose card is open */
  selectedMark: Markup | null = null;
  /** Reject and Mark incorporated ask for a comment or a revision first */
  markAction: 'rejected' | 'incorporated' | null = null;
  /** The review comment, in the app's text area (app-text-area); typing clears the "say why" error */
  readonly markForm = new FormGroup({ comment: new FormControl('') });
  private readonly markCommentTyped = this.markForm.controls.comment.valueChanges.subscribe(() => (this.markError = ''));
  markRevision: string | null = null;
  private reviewBusy = false;
  markError = '';
  showMarkHistory = false;
  /** The open mark's review events; null until they load */
  markEvents: MarkupReviewEvent[] | null = null;
  /** Show my location (spec §16): the browser's fix drawn on a placed sheet */
  showLocation = false;
  /** A quiet line: finding the fix, permission off, and so on */
  locationHint = '';
  /** The latest fix */
  fix: { lat: number; lon: number; accuracy: number | null } | null = null;
  /** The fix on this sheet (x, y can fall outside 0–1) */
  here: SheetPosition | null = null;
  /** Off the sheet: the edge arrow and "You are 240 m east of this sheet" */
  offSheet: (OffSheet & { text: string }) | null = null;
  /** The open mark's outline: the card's overlay is anchored to it */
  markOrigin: HTMLElement | null = null;
  /** Beside the outline (right, then left), else below or above it; the overlay pushes the card into the viewport */
  readonly markCardPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 12 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -12 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  ];
  readonly markCardScroll: ScrollStrategy;
  redlining = false;
  /** W72: the single-sheet download in flight */
  downloadingMode: DrawingDownloadMode | null = null;
  readonly downloadModes: { mode: DrawingDownloadMode; label: string }[] = [
    { mode: 'clean', label: 'Clean' },
    { mode: 'redlines', label: 'With redlines' },
    { mode: 'as_built', label: 'With as-built marks' },
  ];
  banner: 'superseded' | 'withdrawn' | null = null;
  /** Every record linked to this revision (up to MARKER_CAP), newest first: the markers */
  linked: DrawingLinkedRecord[] = [];
  linkedTotal = 0;
  /** "Linked here" pages through the same list on its own */
  panelRows: DrawingLinkedRecord[] = [];
  panelDays: DrawingLinkedDay[] = [];
  panelTotal = 0;
  panelStart = 0;
  panelLoading = false;
  readonly panelPageSize = PANEL_PAGE;
  /** W54: the record filter (Records + Mine only of the Filter (N)); every record by default */
  recordFilter: RecordFilter = ALL_RECORDS;
  /** The record whose peek card is open (also highlighted in Linked here) */
  selected: DrawingLinkedRecord | null = null;
  zoom = 1;
  /** Rendered width of the page at 1x, the base the zoom steps multiply */
  private baseWidth: number | null = null;
  /** W61: the page width at 1× in full screen (fitted to the frame) */
  private fullFit: number | null = null;
  private fullResize: ResizeObserver | null = null;
  /** Add to: waiting for the user to click the spot */
  placing = false;
  /** The page image has loaded; until then Add to places nothing (see sheetPointFromClick) */
  pageLoaded = false;
  /** Add to: the clicked spot, in page space (0–1) */
  pendingPoint: { x: number; y: number } | null = null;
  /**
   * W55: the point a record is being created at, while its create drawer is open: drawn as the pending location pin
   * until the drawer closes (a save turns it into the record's numbered marker)
   */
  drawerPin: { x: number; y: number } | null = null;
  /** W70: where a record's card asked to look, pulsing briefly */
  focusPulse: { x: number; y: number } | null = null;
  private focusPulseTimer: ReturnType<typeof setTimeout> | null = null;
  private unregisterFocus: (() => void) | null = null;
  private readonly sheetFocus = inject(SheetFocusService);
  /** W55: the pan in progress, two-finger pinch state, and the click a drag must not turn into */
  private readonly drag = new DragTracker();
  /** The pointer of the pan in progress, captured once it is a drag (review I2) */
  private dragPointer: number | null = null;
  private readonly safariPinch = new SafariPinch();
  private suppressClick = false;
  private readonly touches = new Map<number, { x: number; y: number }>();
  private pinchStart: { distance: number; zoom: number } | null = null;
  /**
   * W73: where the last press on the sheet began, whether a second finger joined it (a pinch is never a tap), and
   * whether a peek or mark card was open then (the card's outside-click handler closes it before the click lands)
   */
  private press: { x: number; y: number; multi: boolean; cardOpen: boolean } | null = null;
  private readonly zoomReporter = new GestureReporter<'pinch' | 'wheel'>(method => this.track.track('sheet_zoomed', { method }));
  /** This revision's latest placement */
  placement: DrawingPlacement | null = null;
  /** Published map layers showing any revision of this sheet */
  sheetLayers: PublishedMapLayer[] = [];
  /** The map catalog revision the sheet's layers were read from (Remove from map sends it) */
  private catalogRevision: number | null = null;
  /** Shared redlines on the superseded revision still waiting for a carry-forward decision (publishers only) */
  carryCount = 0;
  carryFromLabel: string | null = null;
  /** Edit sheet (configure users): the form's text, stations typed as 120+00 */
  sheetDraft: SheetDraft | null = null;
  /** W64: the form as the drawer opened it, for the saved fields' names */
  private sheetDraftStart: SheetDraft | null = null;
  savingSheet = false;
  sheetError = '';
  readonly statusCopy = STATUS_COPY;
  readonly statusBadge = STATUS_BADGE;
  readonly sheetTypes = SHEET_TYPES;
  /** W54: the viewer's Filter (N) as it is in the URL; applied when a revision opens */
  @Input() viewFilter: SheetViewFilter | null = null;
  @Output() viewFilterChange = new EventEmitter<SheetViewFilter>();
  /** W56: the sheet in the full-screen overlay (the map photo overlay's shell); the wrapper keeps it in the URL (&full=1) */
  @Input() fullScreen = false;
  @Output() fullScreenChange = new EventEmitter<boolean>();
  /** W56: History and Linked here, behind Details in full screen */
  detailsOpen = false;
  /** W60: placing, where the record would go under the pointer (0-1 of the page) */
  ghostPin: { x: number; y: number } | null = null;
  /** A right drawer (Add to, a map layer review) is open: Esc is its own, not the full screen's */
  private drawerOpen = false;
  /** The background elements full screen made inert, to give back */
  private inerted: HTMLElement[] = [];
  private viewFilterSectionsCache: { key: string; sections: MultiSectionFilterSection[] } | null = null;
  readonly markerCap = MARKER_CAP;
  readonly marksCap = RECORD_MARKS_CAP;
  readonly isOpen = isOpenStatus;
  readonly noun = linkedRecordNoun;
  readonly dayTitle = dayTitle;

  readonly markStatusCopy = AS_BUILT_STATUS_COPY;
  readonly markActionCopy = AS_BUILT_ACTION_COPY;
  readonly authorName = authorName;
  /** bbox per row object: a review or reload brings new objects, so nothing goes stale */
  private readonly boxes = new WeakMap<Markup, MarkBox | null>();
  @ViewChild('pageImage') private pageImage?: ElementRef<HTMLImageElement>;
  @ViewChild('pageFrame') private pageFrame?: ElementRef<HTMLElement>;
  @ViewChild('addTrigger') private addTrigger?: MatMenuTrigger;
  @ViewChild('markCard') private markCard?: ElementRef<HTMLElement>;
  @ViewChild(CdkConnectedOverlay) private markOverlay?: CdkConnectedOverlay;
  /** Re-places the card when it grows or shrinks (History, Reject form, a review), so its actions stay in the viewport */
  private markCardResize: ResizeObserver | null = null;
  /** The pending focus-and-observe after the card renders; cleared when it closes */
  private markCardTimer: number | null = null;
  private watchId: number | null = null;
  private proj: Proj4Fn | null = null;
  /** `markUuid` still to focus; cleared after one try so a later reload of the marks does not reopen it */
  private focusMarkUuid: string | null = null;
  private addChosen = false;
  /** One pending right-panel close at a time: a second Add to or review replaces the first's listener */
  private panelClosed: Subscription | null = null;
  private readonly accountUuid: string | null;
  private readonly reset = new Subject<void>();
  private readonly onDestroy = new Subject<void>();

  constructor(
    private api: ProjectDrawingsService,
    private markup: MarkupService,
    private projectsService: ProjectsService,
    private ability: Ability,
    private router: Router,
    auth: AuthService,
    @Optional() @Inject(DRAWING_ADD_TO_PANELS) private panels: Record<AddToType, Type<unknown>> | null,
    @Optional() @Inject(DRAWING_MAP_PANELS) private mapPanels: DrawingMapPanels | null,
    private dialog: MatDialog,
    private manifestApi: ProjectMapManifestService,
    private sources: ProjectMapSourceService,
    private refLayers: ProjectReferenceLayerService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    overlay: Overlay,
    @Inject(PROJ4_LOADER) private proj4Loader: () => Promise<Proj4Fn>,
  ) {
    this.accountUuid = auth.getCurrentUser()?.id ?? null;
    this.markCardScroll = overlay.scrollStrategies.reposition();
    merge(this.projectsService.openRightPanel.pipe(map(() => true)), this.projectsService.closeRightPanel.pipe(map(() => false)))
      .pipe(takeUntil(this.onDestroy))
      .subscribe(open => (this.drawerOpen = open));
  }

  /**
   * W25: the page's untagged and carried rows always; record marks (tagged rows, a full-page PNG each) only for the
   * selected marker, a chip other than All, or Show all record marks, and then the RECORD_MARKS_CAP most recent.
   */
  get markComment(): string {
    return this.markForm.controls.comment.value ?? '';
  }

  set markComment(value: string) {
    this.markForm.controls.comment.setValue(value, { emitEvent: false });
  }

  /** A review on its way: the comment cannot change under it */
  get reviewing(): boolean {
    return this.reviewBusy;
  }

  set reviewing(busy: boolean) {
    this.reviewBusy = busy;
    const comment = this.markForm.controls.comment;
    if (busy) {
      comment.disable({ emitEvent: false });
    } else {
      comment.enable({ emitEvent: false });
    }
  }

  get visibleLayers(): Markup[] {
    return [...this.overlayRows.untagged, ...this.overlayRows.tagged.slice(0, RECORD_MARKS_CAP)];
  }

  /** More record marks than the cap would show */
  get marksCapped(): boolean {
    return this.overlayRows.tagged.length > RECORD_MARKS_CAP;
  }

  /** As-built marks with a preview; every chip shows them */
  get asBuiltLayers(): Markup[] {
    return this.showAsBuilt ? (this.layers || []).filter(l => isAsBuilt(l) && l.preview_url) : [];
  }

  /** Clickable outlines: each visible as-built mark with strokes to outline (inert while Add to places a point) */
  get asBuiltMarks(): { row: Markup; box: MarkBox }[] {
    return this.asBuiltLayers.map(row => ({ row, box: this.boxOf(row) })).filter(m => !!m.box);
  }

  /**
   * W76: another record's tagged marks on screen are clickable by their bounding box: a click opens that record's card,
   * as its numbered marker does (not the selected record's own marks: a click there would close its card)
   */
  get recordMarkHits(): { row: Markup; record: DrawingLinkedRecord; box: MarkBox }[] {
    if (this.redlining) return [];
    const records = new Map(this.linked.map(r => [r.uuid, r]));
    return this.overlayRows.tagged.slice(0, RECORD_MARKS_CAP)
      .filter(row => row.link_uuid !== this.selected?.uuid && records.has(row.link_uuid))
      .map(row => ({ row, record: records.get(row.link_uuid), box: this.boxOf(row) }))
      .filter(m => !!m.box);
  }

  private get overlayRows(): { untagged: Markup[]; tagged: Markup[] } {
    const rows = (this.layers || []).filter(l => !isAsBuilt(l) && l.preview_url && (l.visibility === 'private' ? this.showPrivate : this.showShared));
    // Untagged sheet redlines follow Markup only; Records and Mine only narrow the record (tagged) marks (review I1)
    const untagged = rows.filter(l => !l.link_uuid);
    if (!isAllRecords(this.recordFilter)) {
      // A narrower record filter shows the marks of the records it matches.
      return { untagged, tagged: this.newestFirst(filterLayersBy(rows, this.recordFilter, this.linked, this.accountUuid)) };
    }
    const tagged = this.showAllMarks
      ? this.newestFirst(rows.filter(l => !!l.link_uuid))
      : rows.filter(l => !!this.selected && l.link_uuid === this.selected.uuid);
    return { untagged, tagged };
  }

  /** Tagged rows in the order of their records (newest link first); rows of records not in the list go last. */
  private newestFirst(rows: Markup[]): Markup[] {
    const order = new Map(this.linked.map((r, i) => [r.uuid, i]));
    const rank = (l: Markup) => order.get(l.link_uuid) ?? Number.MAX_SAFE_INTEGER;
    return rows.slice().sort((a, b) => rank(a) - rank(b));
  }

  /**
   * Where the selected record's peek card sits: its point, or for a markup-only location (no pin, so no marker) the top
   * centre of its markup, else the middle of the page
   */
  get peekAt(): { x: number; y: number } | null {
    const record = this.selected;
    if (!record) return null;
    if (record.point) return { x: Number(record.point.x), y: Number(record.point.y) };
    // Its note, or its as-built mark (7.2)
    const row = (this.layers || []).find(l => l.link_uuid === record.uuid);
    const box = row ? this.boxOf(row) : null;
    return box ? { x: (box.x0 + box.x1) / 2, y: box.y0 } : { x: 0.5, y: 0.5 };
  }

  /** Markers for the chosen chip; hidden while the editor is open */
  get markers(): DrawingLinkedRecord[] {
    if (this.redlining) {
      return [];
    }
    return this.linked.filter(r => r.point && recordMatches(r, this.recordFilter, this.accountUuid));
  }

  /** Types the user may create here. Withdrawn revisions take no new links (R27). */
  get addToTypes(): { type: AddToType; label: string }[] {
    if (!this.canRedline || this.redlining) {
      return [];
    }
    return ADD_TO.filter(t => !!this.panels?.[t.type]).filter(t => this.ability.can('create', subject(t.subject, { project_id: this.projectId })));
  }

  get pageWidthPx(): number | null {
    // W61: in full screen 1× is the page fitted to the frame, and every zoom is relative to that fit
    if (this.fullScreen && this.fullFit) {
      return this.fullFit * this.zoom;
    }
    return this.zoom > 1 && this.baseWidth ? this.baseWidth * this.zoom : null;
  }

  /** The page width at 1×: fitted to the full-screen frame, or the docked page as laid out */
  private get zoomBase(): number | null {
    return this.fullScreen ? this.fullFit : this.baseWidth;
  }

  /**
   * W61: full screen's 1× fits (contains) the page in the frame under the floating toolbar and banners, FULL_FIT_MARGIN
   * from each edge and clear of the Details panel. Refitted on resize and when Details opens or closes.
   */
  refitFull(): void {
    const frame = this.pageFrame?.nativeElement;
    const image = this.pageImage?.nativeElement;
    if (!this.fullScreen || !frame || !image) {
      this.fullFit = null;
      return;
    }
    const details = this.detailsOpen && frame.clientWidth > DETAILS_OVERLAPS_BELOW ? DETAILS_PANEL_WIDTH : 0;
    const fit = containWidth(image.naturalWidth, image.naturalHeight, frame.clientWidth - 2 * FULL_FIT_MARGIN - details, frame.clientHeight - 2 * FULL_FIT_MARGIN);
    if (fit && Math.abs(fit - (this.fullFit ?? 0)) >= 0.5) {
      this.fullFit = fit;
      this.cdr.detectChanges();
      this.afterZoom();
    }
  }

  /** Leaving full screen at a zoom: the docked page at 1× as its CSS lays it out (natural size, frame width, window height) */
  private dockedBase(): number | null {
    const frame = this.pageFrame?.nativeElement;
    const image = this.pageImage?.nativeElement;
    if (!frame || !image?.naturalWidth || !image.naturalHeight) {
      return this.baseWidth;
    }
    const box = containWidth(image.naturalWidth, image.naturalHeight, frame.clientWidth - 2 * DOCKED_FRAME_PADDING, window.innerHeight - DOCKED_HEIGHT_RESERVE);
    return box ? Math.min(image.naturalWidth, box) : this.baseWidth;
  }

  /** W73: a record's peek or an as-built mark's card is open */
  private get cardOpen(): boolean {
    return !!this.selected || !!this.selectedMark;
  }

  /**
   * W73: docked at 1×, a click (or Enter / Space, or a tap) on the bare sheet opens full screen. Not beside an open
   * drawer (Add record, Edit sheet): a click there belongs to the drawer's work (review M7).
   */
  get clickToFull(): boolean {
    return !this.fullScreen && this.zoom <= SHEET_ZOOMS[0] && !this.placing && !this.redlining && !this.drawerOpen && this.pageLoaded;
  }

  get canZoomIn(): boolean {
    return this.zoom < SHEET_ZOOMS[SHEET_ZOOMS.length - 1];
  }

  get canZoomOut(): boolean {
    return this.zoom > SHEET_ZOOMS[0];
  }

  /** Before the rows load, the revision's summary (which counts as-built marks as shared) */
  get counts(): { shared: number; mine: number; asBuilt: number } {
    if (!this.layers) {
      return { ...(this.revision?.markup_summary ?? { shared: 0, mine: 0 }), asBuilt: 0 };
    }
    const asBuilt = this.layers.filter(isAsBuilt).length;
    const mine = this.layers.filter(l => l.visibility === 'private').length;
    return { shared: this.layers.length - mine - asBuilt, mine, asBuilt };
  }

  /** The open mark's trail, oldest first */
  get markHistory(): MarkHistoryEntry[] {
    return markHistory(this.selectedMark, this.markEvents);
  }

  /**
   * Status changes the user may make on the open card: publishers only. Mark incorporated needs a later revision of
   * this sheet, so on the latest one (a carried copy on the current revision, say) it is not offered at all.
   */
  get markActions(): AsBuiltReviewAction[] {
    const actions = this.canPublish ? reviewActions(this.selectedMark?.review_status) : [];
    return this.incorporateTargets.length ? actions : actions.filter(a => a !== 'incorporated');
  }

  /** The card's one filled button: the status's natural next step, else none (the rest sit in More) */
  get primaryMarkAction(): AsBuiltReviewAction | null {
    const natural = this.canPublish ? reviewActions(this.selectedMark?.review_status)[0] : undefined;
    return natural && this.markActions.includes(natural) ? natural : null;
  }

  /** The status changes in the card's More menu */
  get moreMarkActions(): AsBuiltReviewAction[] {
    return this.markActions.filter(a => a !== this.primaryMarkAction);
  }

  /** The author's Edit and Delete, while the mark is open; never when the API says the caller does not own it (W59) */
  get canChangeMark(): boolean {
    return this.markRulesAllowChange && this.selectedMark.can_edit !== false;
  }

  /**
   * Delete: the author's while the mark is open; configure users' once it is not (spec §15 rule 5, W30). Not gated on
   * can_edit, which is the edit right only (review I3): the As-built list follows the same rule.
   */
  get canDeleteMark(): boolean {
    return this.markRulesAllowDelete;
  }

  /** W59: Edit / Delete the card would offer, shown off with the reason because the caller does not own the mark */
  get markOwnerLocked(): boolean {
    return this.lockedMarkItems.length > 0;
  }

  get lockedMarkItems(): string[] {
    if (this.selectedMark?.can_edit !== false) {
      return [];
    }
    return this.markRulesAllowChange ? ['Edit'] : [];
  }

  /** Review M1: why Edit is off for this mark */
  get markOwnerCopy(): string {
    return markupLockCopy(this.selectedMark);
  }

  private get markRulesAllowChange(): boolean {
    return canAuthorChange(this.selectedMark, this.accountUuid) && !!this.revision && this.canRedline;
  }

  private get markRulesAllowDelete(): boolean {
    return this.markRulesAllowChange || (this.canConfigure && !!this.selectedMark && this.selectedMark.review_status !== 'open');
  }

  /** Mark incorporated: later issued or superseded revisions of this sheet */
  get incorporateTargets(): { uuid: string; label: string | null }[] {
    return laterRevisions(this.sheet?.revisions, this.revision?.uuid);
  }

  get incorporatedLabel(): string | null {
    const uuid = this.selectedMark?.incorporated_revision_uuid;
    return uuid ? this.sheet?.revisions?.find(r => r.uuid === uuid)?.label ?? '' : null;
  }

  get geo(): SheetGeo | null {
    return this.revision?.geo ?? null;
  }

  /** "Approximate placement (±0.6 m)" while showing the fix on an approximate placement; nothing when verified */
  get approximateText(): string | null {
    const geo = this.geo;
    if (geo?.status !== 'approximate') {
      return null;
    }
    return geo.check_error_m === null || geo.check_error_m === undefined
      ? 'Approximate placement'
      : `Approximate placement (±${Math.round(geo.check_error_m * 10) / 10} m)`;
  }

  /** The accuracy circle's radius in sheet widths */
  get accuracyR(): number {
    return this.here ? Math.min(2, accuracyRadius(this.fix?.accuracy, this.here)) : 0;
  }

  toggleLocation(on: boolean): void {
    if (on) {
      this.startLocation();
    } else {
      this.stopLocation();
    }
  }

  /** The header's title: "5 · PROJECT LAYOUT" (the revision is the line under it) */
  get viewerTitle(): string {
    const sheet = this.revision?.sheet;
    return sheet ? [sheet.number, sheet.title].filter(Boolean).join(' · ') : '';
  }

  /** Discipline and station range under the title */
  get sheetMeta(): string {
    const s = this.sheet;
    return s ? [s.discipline, sheetStationText(s.station_start, s.station_end)].filter(Boolean).join(' · ') : '';
  }

  /** Once this revision's sheet has loaded: the form starts from its discipline and stations, never from blanks */
  get canEditSheet(): boolean {
    return this.canConfigure && !!this.revision?.sheet && this.sheet?.uuid === this.revision.sheet.uuid;
  }

  get canRedline(): boolean {
    return this.revision?.status === 'issued' || this.revision?.status === 'superseded';
  }

  /** The label of the revision this one supersedes; '' until the sheet's history has loaded */
  get previousLabel(): string | null {
    const previous = this.revision?.supersedes_uuid;
    if (!previous) {
      return null;
    }
    return this.sheet?.revisions?.find(r => r.uuid === previous)?.label ?? '';
  }

  get currentLabel(): string | null {
    return this.sheet?.current?.label ?? null;
  }

  get hasOtherCurrent(): boolean {
    const current = this.revision?.sheet?.current_revision_uuid;
    return !!current && current !== this.revision.uuid;
  }

  /**
   * Place on map needs ProjectDrawing publish plus the map layer rights the layer review needs to publish, and a sheet
   * whose type can be placed (plan, plan and profile, other; spec §17 rule 3) once the sheet has loaded.
   */
  get canPlace(): boolean {
    const mapLayer = subject('ProjectMapLayer', { project_id: this.projectId });
    const sheetLoaded = !!this.sheet && this.sheet.uuid === this.revision?.sheet?.uuid;
    return this.canPublish && !!this.mapPanels && this.revision?.status === 'issued' && sheetLoaded && isPlaceableType(this.sheet.sheet_type)
      && this.ability.can('configure', mapLayer) && this.ability.can('publish', mapLayer);
  }

  /**
   * Discard placement (R50): wherever Review placement is offered, and also on a superseded revision whose own
   * placement was left in review when a newer one was issued (W39). That placement can no longer be reviewed or
   * published from here, only dropped; the API needs ProjectDrawing publish.
   */
  get canDiscardPlacement(): boolean {
    if (this.placement?.status !== 'review') {
      return false;
    }
    return this.canPlace || (this.canPublish && this.revision?.status === 'superseded');
  }

  /** Withdraw (Sri): the sheet's current revision, as the register's row menu, for publishers */
  get canWithdrawCurrent(): boolean {
    return this.canPublish && !!this.sheet?.current && !!this.revision?.sheet && this.sheet.uuid === this.revision.sheet.uuid;
  }

  /** Remove from map: the sheet's published layer, for someone who may place it (the API needs ProjectDrawing publish) */
  get canRemoveFromMap(): boolean {
    return this.mapState === 'placed' && this.placement?.status === 'published' && !!this.mapLayer && this.canPublish;
  }

  /** The published layer showing this revision */
  get mapLayer(): PublishedMapLayer | null {
    return this.sheetLayers.find(l => l.drawing.revisionUuid === this.revision?.uuid) ?? null;
  }

  /** Layers still showing an earlier revision of this sheet since a newer one was issued (follow current, R21) */
  get flaggedLayers(): PublishedMapLayer[] {
    return this.sheetLayers.filter(l => l.drawing.needsPlacement && l.drawing.revisionUuid !== this.revision?.uuid);
  }

  /**
   * In order: a placement in flight or waiting for review; a newer revision to place over the flagged layer; on the map;
   * a failed placement to retry; nothing yet. Null when the user can neither place nor see a placement.
   */
  get mapState(): SheetMapState | null {
    const status = this.placement?.status;
    if (status === 'processing' || status === 'review') {
      return status;
    }
    if (this.revision?.status === 'issued' && this.flaggedLayers.length) {
      return 'newer';
    }
    if (this.mapLayer || status === 'published') {
      return 'placed';
    }
    if (!this.canPlace) {
      return null;
    }
    return status === 'failed' ? 'failed' : 'place';
  }

  /** "Placed", or "Placed · Approximate 0.6 m" until a check point is within tolerance. */
  get placedText(): string {
    const p = this.placement;
    if (!p || p.method !== 'similarity' || p.verified) {
      return 'Placed';
    }
    return p.check_error_m === null ? 'Placed · Approximate' : `Placed · Approximate ${p.check_error_m.toFixed(1)} m`;
  }

  /** A placement by stations (spec §17 rule 5): "From 3 stations on Main" in review, "Placed from …" once live (W36) */
  get byStationsText(): string {
    const by = this.placement?.byStations;
    if (!by || (this.mapState !== 'placed' && this.mapState !== 'review')) {
      return '';
    }
    const on = by.alignment ? ` on ${by.alignment}` : '';
    const along = by.stationing === 'chainage' ? ' (stations measured along the line)' : '';
    const lead = this.mapState === 'review' ? 'From' : 'Placed from';
    return `${lead} ${by.points} ${by.points === 1 ? 'station' : 'stations'}${on}${along}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fullScreen && !!changes.fullScreen.previousValue !== this.fullScreen) {
      this.applyFullScreen();
    }
    if (changes.markUuid) {
      this.focusMarkUuid = this.markUuid;
    }
    if (!changes.projectId && !changes.sheetUuid && !changes.revisionUuid) {
      if (changes.markUuid) {
        // Another mark on the revision already open: no reload, just show it.
        setTimeout(() => this.focusRequestedMark());
      }
      return;
    }
    this.reset.next();
    // The old sheet's dot goes at once; the watch stays for the next revision (stopped if it fails to load)
    this.here = null;
    this.offSheet = null;
    if (this.revisionUuid) {
      this.loadRevision(this.revisionUuid);
      return;
    }
    if (this.sheetUuid) {
      this.api.getSheet(this.projectId, this.sheetUuid).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
        next: s => {
          this.sheet = s;
          if (s.current) {
            this.loadRevision(s.current.uuid);
          } else {
            this.notify.notification('This sheet has no published revision yet.', 'error');
          }
        },
        error: e => {
          this.forgetIfGone(e, this.sheetUuid);
          this.fail(e);
        },
      });
    }
  }

  /** A history card's "Sep 30, 2026 · Addendum 1": the separator only between two parts */
  historyWhen(rev: { issued_at: string | null; issue_set?: { name: string } | null }): string {
    return [rev.issued_at ? dayTitle(rev.issued_at) : '', rev.issue_set?.name ?? ''].filter(Boolean).join(' · ');
  }

  ngOnDestroy(): void {
    this.unregisterFocus?.();
    if (this.focusPulseTimer) {
      clearTimeout(this.focusPulseTimer);
    }
    this.fullResize?.disconnect();
    if (this.fullScreen) {
      this.setFullScreenChrome(false);
    }
    this.zoomReporter.flush();
    this.releaseFindDoc();
    this.stopLocation();
    this.clearMarkCardTimer();
    this.markCardResize?.disconnect();
    this.reset.complete();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  toggleRedline(): void {
    if (!this.revision || (!this.redlining && !this.canRedline)) {
      return;
    }
    this.cancelAddTo();
    this.selected = null;
    this.closeMark();
    this.redlining = !this.redlining;
    this.editorConfig = this.buildConfig(this.revision, !this.redlining);
    if (!this.redlining) {
      this.loadLayers();
    }
  }

  onEditorClosed(): void {
    this.redlining = false;
    if (this.revision) {
      this.editorConfig = this.buildConfig(this.revision, true);
      this.loadLayers();
    }
  }

  onEditorSaved(result?: MarkupSaveResult): void {
    const markup = result?.markup;
    if (markup) {
      const sheet_uuid = this.revision?.sheet?.uuid ?? null;
      if (markup.kind === 'as_built') {
        this.track.track('as_built_saved', { sheet_uuid });
      } else {
        const objects = (markup.annotation_data as { objects?: unknown[] } | null)?.objects;
        this.track.track('markup_saved', { sheet_uuid, shared: markup.visibility === 'shared', shapes: Array.isArray(objects) ? objects.length : null });
      }
    }
    this.loadLayers();
  }

  selectMark(row: Markup, event?: Event): void {
    if (this.placing || (event && this.consumeDrag())) {
      return;
    }
    event?.stopPropagation();
    this.selected = null;
    this.resetMarkAction();
    this.showMarkHistory = false;
    this.markEvents = null;
    const wasOpen = !!this.selectedMark;
    this.selectedMark = this.selectedMark?.id === row.id ? null : row;
    this.markOrigin = this.selectedMark
      ? (event?.currentTarget as HTMLElement | null) ?? this.host.nativeElement.querySelector<HTMLElement>(`[data-mark-id="${row.id}"]`)
      : null;
    this.loadMarkEvents();
    if (wasOpen && this.selectedMark) {
      // Another mark while the card is open: the overlay stays attached (no attach event), so focus moves here.
      this.onMarkCardAttached();
    }
  }

  /** Closes the card; focus goes back to the mark's outline when it was inside the card. */
  closeMark(): void {
    const outline = this.markOrigin;
    const hadFocus = !!this.markCard?.nativeElement.contains(document.activeElement);
    this.clearMarkCardTimer();
    this.selectedMark = null;
    this.markOrigin = null;
    this.markEvents = null;
    this.showMarkHistory = false;
    this.resetMarkAction();
    if (hadFocus && outline?.isConnected) {
      outline.focus();
    }
  }

  /** The card takes focus when it opens, so the keyboard lands on its actions rather than the next marker. */
  onMarkCardAttached(): void {
    this.clearMarkCardTimer();
    this.markCardTimer = window.setTimeout(() => {
      this.markCardTimer = null;
      const card = this.markCard?.nativeElement;
      card?.focus();
      this.markCardResize?.disconnect();
      if (card && typeof ResizeObserver !== 'undefined') {
        this.markCardResize = new ResizeObserver(() => this.markOverlay?.overlayRef?.updatePosition());
        this.markCardResize.observe(card);
      }
    });
  }

  private clearMarkCardTimer(): void {
    if (this.markCardTimer) {
      clearTimeout(this.markCardTimer);
      this.markCardTimer = null;
    }
  }

  onMarkCardDetached(): void {
    this.markCardResize?.disconnect();
    this.markCardResize = null;
  }

  /** A click elsewhere closes the card; not one on an outline (it opens that mark) or in another overlay (its menu or dialog). */
  onMarkCardOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.mark-outline, .cdk-overlay-container')) {
      return;
    }
    this.closeMark();
  }

  onMarkCardKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMark();
    }
  }

  /** Mark reviewed and Reopen go straight away; Reject asks for a comment and Mark incorporated for a revision. */
  startReview(action: AsBuiltReviewAction): void {
    if (!this.markActions.includes(action) || this.reviewing) {
      return;
    }
    this.markError = '';
    if (action === 'rejected' || action === 'incorporated') {
      this.markAction = action;
      this.markComment = '';
      const targets = this.incorporateTargets;
      this.markRevision = action === 'incorporated' && targets.length ? targets[0].uuid : null;
      return;
    }
    this.review(action);
  }

  cancelMarkAction(): void {
    this.resetMarkAction();
  }

  submitMarkAction(): void {
    const action = this.markAction;
    const comment = this.markComment.trim();
    if (!action) {
      return;
    }
    if (action === 'rejected' && !comment) {
      this.markError = 'Say why the mark is rejected.';
      return;
    }
    if (action === 'incorporated' && !this.markRevision) {
      this.markError = 'Pick the revision that incorporates the mark.';
      return;
    }
    this.review(action, comment || null, action === 'incorporated' ? this.markRevision : null);
  }

  /** The author edits their open mark in the editor (a PUT on that row) */
  /** W76: the selected record's own marks on this page (its tagged shared row), when there are any */
  get selectedMarkupRow(): Markup | null {
    const record = this.selected;
    return record ? (this.layers || []).find(l => l.link_uuid === record.uuid && l.visibility === 'shared' && !isAsBuilt(l)) ?? null : null;
  }

  /** W76: the peek offers Edit markup for a record whose marks this user may edit (the API's can_edit) */
  get canEditSelectedMarkup(): boolean {
    const row = this.selectedMarkupRow;
    return !!row && row.can_edit !== false && !!this.revision?.preview_url;
  }

  /** W76: the record's marks in the editor (its tagged row, the record's point as the context pin) */
  editRecordMarkup(): void {
    const record = this.selected;
    if (!this.revision || !record || !this.canEditSelectedMarkup) {
      return;
    }
    this.cancelAddTo();
    this.closeMark();
    this.selected = null;
    this.redlining = true;
    this.editorConfig = {
      ...this.buildConfig(this.revision, false), linkUuid: record.uuid,
      ...(record.point ? { contextPin: { x: Number(record.point.x), y: Number(record.point.y) } } : {}),
      // A markup-only location (no pin): its markup is the location, removed from the record, not emptied here
      ...(record.point ? {} : { emptySaveError: PINLESS_EMPTY_HERE }),
    };
  }

  editMark(): void {
    const row = this.selectedMark;
    if (!row || !this.canChangeMark) {
      return;
    }
    this.cancelAddTo();
    this.closeMark();
    this.redlining = true;
    this.editorConfig = { ...this.buildConfig(this.revision, false), asBuiltMarkup: row };
  }

  deleteMark(): void {
    const row = this.selectedMark;
    if (!row || !this.canDeleteMark) {
      return;
    }
    const model = new ConfirmDialogModel('Delete as-built mark', deleteMarkMessage(this.revision?.sheet?.number, this.revision?.label), 'Delete', 'Cancel', true);
    this.dialog.open(ConfirmDialogComponent, { data: model }).afterClosed().pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe(ok => {
      if (!ok) {
        return;
      }
      this.markup.delete(row.id).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
        next: () => {
          this.closeMark();
          this.notify.notification('As-built mark deleted', 'success');
          this.loadLayers();
        },
        error: e => {
          this.markError = this.api.failureMessage(e?.error?.code, this.api.errorMessage(e));
          this.loadLayers();
        },
      });
    });
  }

  private review(status: AsBuiltReviewAction, comment: string | null = null, revisionUuid: string | null = null): void {
    const row = this.selectedMark;
    if (!row) {
      return;
    }
    this.reviewing = true;
    this.markError = '';
    const body = { status, ...(comment ? { comment } : {}), ...(revisionUuid ? { incorporated_revision_uuid: revisionUuid } : {}) };
    this.markup.review(row.id, body).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: updated => {
        this.reviewing = false;
        this.resetMarkAction();
        // The author never changes: keep it should the answer come without it.
        const merged = Object.assign(updated, { createdBy: updated.createdBy ?? row.createdBy });
        this.layers = (this.layers || []).map(l => (l.id === merged.id ? merged : l));
        if (this.selectedMark?.id === merged.id) {
          this.selectedMark = merged;
        }
        this.notify.notification(`As-built mark ${AS_BUILT_STATUS_COPY[merged.review_status ?? status].toLowerCase()}`, 'success');
        this.track.track('as_built_reviewed', { status: merged.review_status ?? status, count: 1, source: 'viewer' });
        this.loadMarkEvents();
      },
      error: e => {
        this.reviewing = false;
        const code = e?.error?.code;
        this.markError = this.api.failureMessage(code, this.api.errorMessage(e));
        if (code === 'MARKUP_REVIEW_TRANSITION_INVALID' || code === 'MARKUP_AS_BUILT_LOCKED') {
          // Someone else reviewed it meanwhile: drop the form, show the status they set and its trail.
          const message = this.markError;
          this.resetMarkAction();
          this.markError = message;
          this.loadLayers();
          this.loadMarkEvents();
        }
      },
    });
  }

  /** The open mark's trail; a late answer for a mark no longer open is dropped. History is a hint: a failure leaves it at Recorded. */
  private loadMarkEvents(): void {
    const id = this.selectedMark?.id;
    if (!id) {
      return;
    }
    this.markup.reviewEvents(id).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: events => {
        if (this.selectedMark?.id === id) {
          this.markEvents = events;
        }
      },
      error: () => undefined,
    });
  }

  private resetMarkAction(): void {
    this.markAction = null;
    this.markComment = '';
    this.markRevision = null;
    this.markError = '';
  }

  private boxOf(row: Markup): MarkBox | null {
    if (!this.boxes.has(row)) {
      this.boxes.set(row, markupBBox(row.annotation_data, row.canvas_width, row.canvas_height));
    }
    return this.boxes.get(row);
  }

  /** W65: who uploads sets (configure, as Upload set) once the sheet has loaded */
  get canUploadRevision(): boolean {
    return this.canEditSheet;
  }

  /**
   * W65: the upload dialog for this sheet: the API binds the PDF's page 1 to it (replaces, kept through inspect),
   * brings any other pages in skipped and names the set for it. The set's review opens next, as after Upload set.
   */
  uploadRevision(): void {
    const s = this.sheet;
    if (!this.canUploadRevision || !s) {
      return;
    }
    const data: DrawingUploadDialogData = { projectId: this.projectId, sheet: { uuid: s.uuid, number: s.number, label: this.editSheetLabel } };
    this.dialog
      .open(DrawingUploadDialogComponent, { data, panelClass: ['floating-dialog-box', 'drawing-upload-dialog'] })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((set: { uuid?: string } | null) => {
        if (set?.uuid) {
          this.openSet.emit(set.uuid);
        }
      });
  }

  /** W64: "4 · PROJECT LAYOUT", under the drawer's Edit sheet heading */
  get editSheetLabel(): string {
    const s = this.sheet;
    return [s?.number, s?.title].filter(Boolean).join(' · ');
  }

  /** W64: Edit sheet opens in the right drawer (as Add record), the sheet staying in view beside it */
  startEditSheet(): void {
    if (!this.canEditSheet) {
      return;
    }
    // Review M3: one drawer at a time; the one open (Add record's form) closes first, asking if it has changes
    if (this.drawerOpen && !this.sheetDraft) {
      this.closeDrawerThen(() => this.startEditSheet());
      return;
    }
    const r = this.revision;
    const s = this.sheet;
    this.sheetDraft = {
      number: s.number ?? r.sheet.number ?? '',
      title: s.title ?? r.sheet.title ?? '',
      discipline: s.discipline ?? '',
      // A stored 'other' is the same as none: the form shows both as Other
      sheetType: s.sheet_type && s.sheet_type !== 'other' ? s.sheet_type : '',
      start: stationInputText(s.station_start),
      end: stationInputText(s.station_end),
    };
    this.sheetDraftStart = { ...this.sheetDraft };
    this.sheetError = '';
    this.onPanelClosed(() => {
      this.sheetDraft = null;
      this.sheetError = '';
    });
    this.projectsService.openRightPanel.next({ component: DrawingSheetEditPanelComponent, data: { host: this } });
  }

  /** Closes the open right drawer (its close listener runs, e.g. the Add record pin clears), asking first when it has changes */
  private closeDrawerThen(then: () => void): void {
    const close = () => {
      // Review N3: the next drawer opens once the wrapper has really closed this one (its route update done)
      const closed = this.projectsService.rightPanelClosed;
      if (closed) {
        closed.pipe(take(1), takeUntil(this.onDestroy)).subscribe(() => then());
      }
      this.projectsService.closeRightPanel.next();
      if (!closed) {
        then();
      }
    };
    const dirty = this.projectsService.isAnyFormDirty;
    if (!dirty?.getValue?.()) {
      close();
      return;
    }
    const model = new ConfirmDialogModel('Unsaved changes', 'There are unsaved changes.<br/>Are you sure you want to discard?');
    this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: model }).afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(discard => {
      if (discard) {
        dirty.next(false);
        close();
      }
    });
  }

  /** The drawer's Cancel (its ✕ closes it the same way) */
  cancelEditSheet(): void {
    const open = !!this.sheetDraft;
    this.sheetDraft = null;
    this.sheetError = '';
    if (open) {
      this.projectsService.closeRightPanel.next();
    }
  }

  /** The drawer's Save: its form, saved as Edit sheet always has */
  saveSheetFrom(draft: SheetDraft): void {
    this.sheetDraft = draft;
    this.saveSheet();
  }

  /** PUT /sheets/:uuid with the whole form: blank discipline and stations clear them. */
  saveSheet(): void {
    const draft = this.sheetDraft;
    const sheetUuid = this.revision?.sheet?.uuid;
    if (!draft || !sheetUuid || !this.canEditSheet || this.savingSheet) {
      return;
    }
    const number = draft.number.trim();
    const start = parseStationInput(draft.start);
    const end = parseStationInput(draft.end);
    if (!number) {
      this.sheetError = 'Enter a sheet number.';
      return;
    }
    if (start === undefined || end === undefined) {
      this.sheetError = 'Enter a station like 120+00.';
      return;
    }
    this.savingSheet = true;
    this.sheetError = '';
    const patch = {
      number, title: draft.title.trim(), discipline: draft.discipline.trim() || null, sheet_type: draft.sheetType || null, station_start: start, station_end: end,
    };
    this.api.updateSheet(this.projectId, sheetUuid, patch).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: updated => {
        this.savingSheet = false;
        this.sheetDraft = null;
        // W64: which fields changed, by name only
        const before = this.sheetDraftStart;
        const fields = before ? SHEET_DRAFT_FIELDS.filter(([k]) => draft[k].trim() !== before[k].trim()).map(([, name]) => name) : [];
        this.track.track('sheet_details_saved', { fields });
        this.projectsService.closeRightPanel.next();
        this.sheet = { ...this.sheet, ...updated, revisions: updated?.revisions ?? this.sheet?.revisions ?? [] };
        if (this.revision?.sheet?.uuid === sheetUuid) {
          this.revision = { ...this.revision, sheet: { ...this.revision.sheet, number: this.sheet.number, title: this.sheet.title } };
        }
        this.notify.notification('Sheet saved', 'success');
      },
      error: e => {
        this.savingSheet = false;
        this.sheetError = this.api.failureMessage(e?.error?.code, this.api.errorMessage(e));
      },
    });
  }

  zoomBy(step: number): void {
    // W55: the next step of the ladder, also from a pinch that left the zoom between steps
    const next = stepZoom(SHEET_ZOOMS, this.zoom, step > 0 ? 1 : -1);
    this.track.track('sheet_zoomed', { method: 'buttons' });
    // W66: as on the map, + / − zoom around the middle of what is in view (pinch and ctrl + wheel keep the cursor)
    const frame = this.pageFrame?.nativeElement;
    if (frame && this.pageLoaded && this.pageImage) {
      const box = frame.getBoundingClientRect();
      this.zoomAt(next, box.left + frame.clientWidth / 2, box.top + frame.clientHeight / 2);
      return;
    }
    if (this.zoom === 1 && !this.fullScreen) {
      this.baseWidth = this.pageImage?.nativeElement.clientWidth || this.baseWidth;
    }
    this.zoom = this.zoomBase ? next : 1;
    this.afterZoom();
  }

  /** The outline moved with the page: re-place the open mark card once the new size has rendered */
  private afterZoom(): void {
    if (this.selectedMark) {
      // The outline moved with the page: re-place the card once the new size has rendered.
      this.cdr.detectChanges();
      this.markOverlay?.overlayRef?.updatePosition();
    }
  }

  /**
   * W55: zoom to `zoom` (clamped to the ladder's range) keeping the page point under the cursor where it is. Used by
   * ctrl / ⌘ + wheel, the trackpad pinch and the two-finger touch pinch.
   */
  zoomAt(zoom: number, clientX: number, clientY: number): void {
    const image = this.pageImage?.nativeElement;
    const frame = this.pageFrame?.nativeElement;
    if (!image || !frame || !this.pageLoaded) {
      return;
    }
    if (this.zoom === 1 && !this.fullScreen) {
      this.baseWidth = image.clientWidth || this.baseWidth;
    }
    if (!this.zoomBase) {
      return;
    }
    const next = clampZoom(zoom, SHEET_ZOOMS[0], SHEET_ZOOMS[SHEET_ZOOMS.length - 1]);
    if (Math.abs(next - this.zoom) < 1e-3) {
      return;
    }
    const before = image.getBoundingClientRect();
    const fx = pageFraction(clientX, before.left, before.width);
    const fy = pageFraction(clientY, before.top, before.height);
    this.zoom = next;
    this.cdr.detectChanges();
    const after = image.getBoundingClientRect();
    frame.scrollLeft += anchorScrollDelta(fx, clientX, after.left, after.width);
    frame.scrollTop += anchorScrollDelta(fy, clientY, after.top, after.height);
    this.afterZoom();
  }

  /** ctrl / ⌘ + wheel and the trackpad pinch zoom at the cursor; a plain wheel scrolls as ever */
  onFrameWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    if (this.safariPinch.active) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.zoomAt(this.zoom * wheelZoomFactor(event.deltaY), event.clientX, event.clientY);
    this.zoomReporter.step(isPinchWheel(event) ? 'pinch' : 'wheel');
  }

  /** A press on the sheet: the start of a pan (mouse, zoomed in) or of a two-finger pinch (touch) */
  onFramePointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.touches.size === 1) {
        this.press = { x: event.clientX, y: event.clientY, multi: false, cardOpen: this.cardOpen };
      } else if (this.press) {
        this.press.multi = true;
      }
      // One source per pinch (review N2): not while Safari's gesture events already drive it
      if (this.touches.size === 2 && !this.safariPinch.active) {
        const [a, b] = Array.from(this.touches.values());
        this.pinchStart = { distance: pinchDistance(a, b), zoom: this.zoom };
      }
      return;
    }
    if (event.button === 0) {
      this.press = { x: event.clientX, y: event.clientY, multi: false, cardOpen: this.cardOpen };
    }
    const target = event.target as HTMLElement | null;
    if (event.button !== 0 || this.zoom <= 1 || target?.closest('.peek, input, textarea, select')) {
      return;
    }
    this.drag.down(event.clientX, event.clientY);
    this.dragPointer = event.pointerId;
  }

  onFramePointerMove(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      if (!this.touches.has(event.pointerId)) {
        return;
      }
      this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.touches.size === 2 && this.pinchStart) {
        const [a, b] = Array.from(this.touches.values());
        this.zoomAt(this.pinchStart.zoom * (pinchDistance(a, b) / this.pinchStart.distance), (a.x + b.x) / 2, (a.y + b.y) / 2);
        this.zoomReporter.step('pinch');
      }
      return;
    }
    if (!this.drag.active) {
      return;
    }
    const frame = this.pageFrame?.nativeElement;
    if (buttonsReleased(event)) {
      // Released outside the frame before the pan had the pointer: the pan is over, and no click is coming
      this.endPan(frame);
      return;
    }
    const { dx, dy } = this.drag.move(event.clientX, event.clientY);
    if (this.drag.dragging && this.dragPointer !== null) {
      capturePointer(frame, this.dragPointer);
    }
    if (frame && (dx || dy)) {
      frame.scrollLeft += dx;
      frame.scrollTop += dy;
    }
  }

  onFramePointerUp(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.touches.delete(event.pointerId);
      if (this.touches.size < 2) {
        this.pinchStart = null;
      }
      return;
    }
    releasePointer(this.pageFrame?.nativeElement, event.pointerId);
    this.dragPointer = null;
    if (this.drag.up()) {
      // The click that ends a pan must not place the Add to point or pick a marker
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false));
    }
  }

  /** The click just ended a pan */
  /** The frame lost the pointer (released elsewhere, the window lost focus): the pan ends there */
  onFrameCaptureLost(): void {
    if (this.drag.active) {
      this.endPan(this.pageFrame?.nativeElement);
    }
  }

  /** Review M5: desktop Safari's trackpad pinch (gesture events) zooms at its centre, as ctrl + wheel does elsewhere */
  onFrameGestureStart(event: Event): void {
    this.safariPinch.start(event, this.zoom, !!this.pinchStart);
  }

  onFrameGestureChange(event: Event): void {
    const zoom = this.safariPinch.change(event);
    if (zoom !== null && isSafariGesture(event)) {
      this.zoomAt(zoom, event.clientX, event.clientY);
      this.zoomReporter.step('pinch');
    }
  }

  onFrameGestureEnd(event: Event): void {
    this.safariPinch.end(event);
  }

  private endPan(frame: HTMLElement | undefined): void {
    if (this.dragPointer !== null) {
      releasePointer(frame, this.dragPointer);
    }
    this.dragPointer = null;
    this.drag.cancel();
  }

  private consumeDrag(): boolean {
    if (!this.suppressClick) {
      return false;
    }
    this.suppressClick = false;
    return true;
  }

  /** The record types the Records section offers: the known ones, then any other type linked on this sheet */
  get recordTypeOptions(): { value: string; label: string }[] {
    const known = new Set(RECORD_TYPE_LABELS.map(t => t.value));
    const extra = Array.from(new Set(this.linked.map(r => r.entity_type).filter(t => !!t && !known.has(t))));
    return [...RECORD_TYPE_LABELS, ...extra.map(t => ({ value: t, label: linkedRecordNoun(t) }))];
  }

  /**
   * W54: the viewer's Filter (N) (the shared multi-section filter, as the register): Markup (Shared · Private · As-built),
   * Records (one per type) and Show (Mine only · All record markup · My location when placed). Markup and Records are
   * all ticked by default (no filter); Show's items are off by default and count when on (countWhenAll).
   */
  get viewFilterSections(): MultiSectionFilterSection[] {
    const types = this.recordTypeOptions;
    const key = JSON.stringify([this.showShared, this.showPrivate, this.showAsBuilt, this.recordFilter, this.showAllMarks, this.showLocation,
      !!this.geo, types.map(t => t.value)]);
    if (this.viewFilterSectionsCache?.key !== key) {
      const shownTypes = this.recordFilter.types;
      this.viewFilterSectionsCache = {
        key,
        sections: [
          {
            key: 'markup', label: 'Markup', options: [
              { value: 'shared', label: 'Shared', isChecked: this.showShared },
              { value: 'private', label: 'Private', isChecked: this.showPrivate },
              { value: 'as_built', label: 'As-built', isChecked: this.showAsBuilt },
            ],
          },
          {
            key: 'records', label: 'Records', searchable: types.length > 8,
            options: types.map(t => ({ value: t.value, label: t.label, isChecked: shownTypes === null || shownTypes.includes(t.value) })),
          },
          {
            key: 'show', label: 'Show', countWhenAll: true, hideAll: true, options: [
              { value: 'mine', label: 'Mine only', isChecked: this.recordFilter.mineOnly },
              { value: 'all_marks', label: 'All record markup', isChecked: this.showAllMarks },
              ...(this.geo ? [{ value: 'location', label: 'My location', isChecked: this.showLocation }] : []),
            ],
          },
        ],
      };
    }
    return this.viewFilterSectionsCache.sections;
  }

  /** A Filter (N) section changed: apply it, keep it in the URL and report which sections filter */
  onViewFilterChange(event: { key: string; values: string[] }): void {
    const values = event.values ?? [];
    if (event.key === 'markup') {
      this.showShared = values.includes('shared');
      this.showPrivate = values.includes('private');
      this.showAsBuilt = values.includes('as_built');
      if (!this.showAsBuilt) {
        this.closeMark();
      }
    } else if (event.key === 'records') {
      const all = this.recordTypeOptions.map(t => t.value);
      const types = all.every(t => values.includes(t)) ? null : values;
      this.setRecordFilter({ ...this.recordFilter, types });
    } else if (event.key === 'show') {
      this.showAllMarks = values.includes('all_marks');
      this.setRecordFilter({ ...this.recordFilter, mineOnly: values.includes('mine') });
      if (this.geo && values.includes('location') !== this.showLocation) {
        this.toggleLocation(values.includes('location'));
      }
    } else {
      return;
    }
    const state = this.currentViewFilter;
    const sections = [
      ...(!state.markup ? [] : ['markup']), ...(state.records ? ['records'] : []), ...(state.show.length || this.showLocation ? ['show'] : []),
    ];
    this.track.track('viewer_filter_changed', { sections, count: sections.length });
    this.viewFilterChange.emit(state);
  }

  /** The record filter changed: a selected record it now hides is let go */
  setRecordFilter(filter: RecordFilter): void {
    this.recordFilter = filter;
    if (this.selected && !recordMatches(this.selected, filter, this.accountUuid)) {
      this.selected = null;
    }
  }

  /** The Filter (N) as the URL keeps it (null / [] for the defaults); My location is never kept (it asks the browser) */
  get currentViewFilter(): SheetViewFilter {
    const markup = this.showShared && this.showPrivate && this.showAsBuilt ? null
      : [...(this.showShared ? ['shared'] : []), ...(this.showPrivate ? ['private'] : []), ...(this.showAsBuilt ? ['as_built'] : [])];
    return {
      markup,
      records: this.recordFilter.types,
      show: [...(this.recordFilter.mineOnly ? ['mine'] : []), ...(this.showAllMarks ? ['all_marks'] : [])],
    };
  }

  /** The URL's Filter (N) (or the defaults) when a revision opens */
  private applyViewFilter(state: SheetViewFilter | null): void {
    const markup = state?.markup ?? null;
    this.showShared = markup === null || markup.includes('shared');
    this.showPrivate = markup === null || markup.includes('private');
    this.showAsBuilt = markup === null || markup.includes('as_built');
    this.showAllMarks = !!state?.show?.includes('all_marks');
    this.recordFilter = { types: state?.records ?? null, mineOnly: !!state?.show?.includes('mine') };
  }

  selectMarker(row: DrawingLinkedRecord, event?: Event): void {
    if (this.placing || this.consumeDrag()) {
      return;
    }
    event?.stopPropagation();
    this.closeMark();
    this.selected = this.selected?.uuid === row.uuid ? null : row;
  }

  /** A Linked here row: show its marker (switching to All when the chip hides it) and its peek card. */
  selectFromPanel(row: DrawingLinkedRecord): void {
    if (!recordMatches(row, this.recordFilter, this.accountUuid)) {
      this.recordFilter = ALL_RECORDS;
      this.viewFilterChange.emit(this.currentViewFilter);
    }
    this.closeMark();
    this.selected = row;
    setTimeout(() => {
      // A markup-only record has no marker: its peek card is scrolled to instead
      const marker = document.querySelector(`[data-marker-uuid="${row.uuid}"]`) ?? (row.point ? null : document.querySelector('[data-testid="drawing-viewer-peek"]'));
      marker?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
    });
  }

  closePeek(): void {
    this.selected = null;
  }

  canOpen(row: DrawingLinkedRecord | null): boolean {
    return !!row && !!linkedRecordRoute(this.projectId, row);
  }

  openRecord(row: DrawingLinkedRecord): void {
    const route = linkedRecordRoute(this.projectId, row);
    if (route) {
      void this.router.navigate(route.commands, { queryParams: route.queryParams });
    }
  }

  /** 7.2: the selected as-built mark was made from a record: "From <record>" on its card ('' otherwise) */
  get selectedMarkRecord(): string {
    return markRecordText(this.selectedMark?.linked_record);
  }

  get canOpenMarkRecord(): boolean {
    return !!markRecordRoute(this.projectId, this.selectedMark?.linked_record);
  }

  openMarkRecord(): void {
    const route = markRecordRoute(this.projectId, this.selectedMark?.linked_record);
    if (route) {
      void this.router.navigate(route.commands, { queryParams: route.queryParams });
    }
  }

  onPanelPage(start: number): void {
    this.loadPanel(start);
  }

  startAddTo(): void {
    if (!this.addToTypes.length) {
      return;
    }
    this.selected = null;
    this.closeMark();
    this.pendingPoint = null;
    this.ghostPin = null;
    this.placing = !this.placing;
    if (this.placing) {
      this.track.track('add_record_started');
    }
  }

  cancelAddTo(): void {
    this.placing = false;
    this.pendingPoint = null;
    this.ghostPin = null;
  }

  /** W60: while placing, the ghost pin follows a mouse or pen over the sheet (a touch places where it taps) */
  onPagePointerMove(event: PointerEvent): void {
    const image = this.pageImage?.nativeElement;
    if (!this.placing || this.pendingPoint || event.pointerType === 'touch' || !image || !this.pageLoaded) {
      this.ghostPin = null;
      return;
    }
    const box = image.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width;
    const y = (event.clientY - box.top) / box.height;
    this.ghostPin = box.width && x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null;
  }

  /** A click on the page: places the Add to point, or closes the peek card. */
  onPageClick(event: MouseEvent): void {
    if (this.consumeDrag()) {
      return;
    }
    if (!this.placing) {
      // W73: with a peek or mark card open the click only closes it; otherwise, docked at 1×, it opens full screen
      const closing = this.cardOpen || !!this.press?.cardOpen;
      this.selected = null;
      this.closeMark();
      if (!closing && this.clickToFull && this.wasTap(event)) {
        this.toggleFullScreen(true, 'click');
      }
      this.press = null;
      return;
    }
    const point = sheetPointFromClick(event);
    if (!point) {
      return;
    }
    this.ghostPin = null;
    this.pendingPoint = point;
    this.addChosen = false;
    // The menu's anchor moves to the point first.
    setTimeout(() => this.addTrigger?.openMenu());
  }

  /** W73: the press behind this click stayed put (within the drag threshold) and was not a pinch */
  private wasTap(event: MouseEvent): boolean {
    const press = this.press;
    this.press = null;
    // No press seen (a click from assistive tech): it is a tap
    return !press || (!press.multi && Math.hypot(event.clientX - press.x, event.clientY - press.y) <= DRAG_THRESHOLD_PX);
  }

  /**
   * Keyboard placement while adding: arrows move the point 1% of the page, Enter or Space opens the type menu.
   * W73: docked at 1×, Enter or Space opens full screen.
   */
  onPageKey(event: KeyboardEvent): void {
    if (this.clickToFull && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.toggleFullScreen(true, 'keyboard');
      return;
    }
    if (!this.placing || !this.pageLoaded) {
      return;
    }
    const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = steps[event.key];
    if (!step && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const from = this.pendingPoint ?? { x: 0.5, y: 0.5 };
    this.pendingPoint = step ? { x: clamp(from.x + step[0] / 100), y: clamp(from.y + step[1] / 100) } : from;
    if (!step) {
      this.addChosen = false;
      setTimeout(() => this.addTrigger?.openMenu());
    }
  }

  /** The page image finished loading: Add to may place a point. */
  onPageLoad(): void {
    this.pageLoaded = true;
    if (this.fullScreen && !this.inerted.length) {
      // Opened in full screen (reload with full=1, a phone): the overlay is drawn now
      this.setBackgroundInert(true);
    }
    if (this.fullScreen) {
      // W61: the page's own size is known now: fit it, and follow the frame from here
      this.refitFull();
      this.watchFullResize(true);
    }
    if (this.fix) {
      this.placeFix();
    }
    if (this.focusMarkUuid) {
      setTimeout(() => this.focusRequestedMark());
    }
  }

  /**
   * Starts watching the browser's position once proj4 has loaded. Nothing is sent anywhere: the fix is placed on the
   * sheet in the browser (spec §16 rule 7).
   */
  private startLocation(): void {
    const geolocation = typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
    if (!this.geo) {
      return;
    }
    if (!geolocation) {
      this.showLocation = false;
      this.locationHint = 'This browser cannot share its location.';
      return;
    }
    // Browsers refuse location on plain http (it would read as "permission denied"): say why instead
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      this.showLocation = false;
      this.locationHint = 'Location needs a secure (https) connection to this site.';
      return;
    }
    this.showLocation = true;
    this.locationHint = 'Finding your location…';
    this.proj4Loader().then(
      proj => {
        this.proj = proj;
        if (!this.showLocation || this.watchId !== null) {
          return;
        }
        this.watchId = geolocation.watchPosition(
          p => this.onFix(p),
          e => this.onFixError(e),
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 },
        );
        this.cdr.markForCheck();
      },
      () => {
        this.stopLocation();
        this.locationHint = 'Your location could not be shown. Try again.';
        this.cdr.markForCheck();
      },
    );
  }

  /** Stops watching (toggle off, another revision with no position, leaving the viewer) */
  private stopLocation(): void {
    if (this.watchId !== null && typeof navigator !== 'undefined') {
      navigator.geolocation?.clearWatch(this.watchId);
    }
    this.watchId = null;
    this.showLocation = false;
    this.fix = null;
    this.here = null;
    this.offSheet = null;
    this.locationHint = '';
  }

  private onFix(p: GeolocationPosition): void {
    if (!this.showLocation) {
      return;
    }
    this.fix = { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : null };
    this.locationHint = '';
    this.placeFix();
    this.cdr.markForCheck();
  }

  /** Permission off is a quiet hint, not an error; a slow or lost fix keeps waiting */
  private onFixError(e: GeolocationPositionError): void {
    if (e.code === e.PERMISSION_DENIED) {
      this.stopLocation();
      this.locationHint = 'Location is off for this site. Allow it in the browser to see where you are on the sheet.';
    } else if (!this.fix) {
      this.locationHint = 'Waiting for your location…';
    }
    this.cdr.markForCheck();
  }

  /** The fix on the current revision's sheet: the dot inside, the edge arrow and distance outside */
  private placeFix(): void {
    const geo = this.geo;
    const fix = this.fix;
    this.here = fix && geo && this.proj ? projectToSheet(fix.lat, fix.lon, geo, this.proj) : null;
    if (this.here && !this.here.inside) {
      const text = offSheetText(fix.lat, fix.lon, this.here, geo, this.proj) ?? 'You are off this sheet';
      const img = this.pageImage?.nativeElement;
      // The arrow turns on the page as drawn: the angle needs its aspect (square until the image has loaded)
      const aspect = img?.naturalWidth && img?.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
      this.offSheet = { ...offSheetBearing(this.here.x, this.here.y, aspect), text };
    } else {
      this.offSheet = null;
    }
  }

  /** Opens `markUuid`'s card once the page image and the marks are in: shows the As-built layer, scrolls to the outline. */
  private focusRequestedMark(): void {
    const uuid = this.focusMarkUuid;
    if (!uuid || !this.layers || !this.pageLoaded) {
      return;
    }
    this.focusMarkUuid = null;
    const row = this.layers.find(l => l.id === uuid && isAsBuilt(l));
    if (!row) {
      this.notify.notification('That as-built mark is no longer on this sheet.', 'error');
      return;
    }
    this.showAsBuilt = true;
    this.cdr.detectChanges();
    const outline = this.host.nativeElement.querySelector<HTMLElement>(`[data-mark-id="${uuid}"]`);
    if (!outline) {
      this.notify.notification('That as-built mark has no preview yet, so it cannot be shown on the sheet.', 'error');
      return;
    }
    outline.scrollIntoView?.({ block: 'center', inline: 'center' });
    if (this.selectedMark?.id !== row.id) {
      this.selectMark(row);
    }
    this.cdr.markForCheck();
  }

  onAddMenuClosed(): void {
    if (!this.addChosen) {
      this.pendingPoint = null;
    }
  }

  /**
   * Opens the record's own create panel with the sheet link prefilled. The forms close the panel after a save, so
   * the close reloads the markers and a new link means the record was added.
   */
  addTo(type: AddToType): void {
    const r = this.revision;
    const point = this.pendingPoint;
    if (!r || !point || !this.addToTypes.some(t => t.type === type)) {
      return;
    }
    this.addChosen = true;
    this.track.track('add_record_placed', { type });
    const link = draftSheetLink(r, this.sheet, point, null);
    const before = new Set(this.linked.map(l => l.uuid));
    this.placing = false;
    this.pendingPoint = null;
    // W55: the point stays on the sheet as the pending pin while the drawer is open, and in view beside it
    this.drawerPin = point;
    const open = (component: unknown, data: Record<string, unknown>) => {
      this.onPanelClosed(() => {
        this.drawerPin = null;
        this.loadLinked(before);
      });
      this.projectsService.openRightPanel.next({ component, data });
      setTimeout(() => this.revealDrawerPin(), 350);
    };
    if (type === 'issue') {
      open(this.panels.issue, { projectId: this.projectId, entity_type: 'project', entity_id: this.projectId, map_feature_link: link });
    } else if (type === 'rfi') {
      // The RFI effect would otherwise switch to the RFIs tab on save and drop the sheet (W20).
      open(this.panels.rfi, { map_feature_link: link, stayInPlace: true });
    } else {
      this.projectsService.fetchPunchlist({ projectId: this.projectId }).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
        next: (resp: { data?: unknown }) => {
          const list = (Array.isArray(resp?.data) ? resp.data : resp?.data ? [resp.data] : []) as Record<string, unknown>[];
          const punchlists = list.map(p => (p?.attributes ? { ...(p.attributes as object), id: p.id, uuid: p.id } : p));
          if (!punchlists.length) {
            this.notify.notification('This project has no punch lists yet. Add one on the Punch list tab first.', 'error');
            return;
          }
          open(this.panels.punch, { projectId: this.projectId, punchlists, map_feature_link: link });
        },
        error: e => this.fail(e),
      });
    }
  }

  /** W67: from History, this revision against another one; the older one is always Rev A (History lists newest first) */
  compareWith(otherUuid: string): void {
    const list = this.sheet?.revisions ?? [];
    const here = this.revision?.uuid;
    const mine = list.findIndex(r => r.uuid === here);
    const theirs = list.findIndex(r => r.uuid === otherUuid);
    if (!here || theirs < 0 || otherUuid === here) {
      return;
    }
    const otherIsOlder = mine < 0 || theirs > mine;
    this.compare.emit(otherIsOlder ? { revision: here, against: otherUuid } : { revision: otherUuid, against: here });
  }

  openCompare(): void {
    const r = this.revision;
    if (r?.supersedes_uuid) {
      this.track.track('revision_compare_opened', { sheet_uuid: r.sheet?.uuid ?? null });
      this.compare.emit({ revision: r.uuid, against: r.supersedes_uuid });
    }
  }

  reviewCarry(): void {
    const sheetUuid = this.revision?.sheet?.uuid;
    if (sheetUuid) {
      this.openCarry.emit(sheetUuid);
    }
  }

  openCurrent(): void {
    if (this.hasOtherCurrent) {
      this.openRevision.emit(this.revision.sheet.current_revision_uuid);
    }
  }

  selectRevision(uuid: string): void {
    if (uuid !== this.revision?.uuid) {
      this.openRevision.emit(uuid);
    }
  }

  private loadRevision(uuid: string): void {
    this.api.getRevision(this.projectId, uuid).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: r => {
        // Another page image loads again; the same one (a reload of this revision) fires no new load event.
        this.pageLoaded = this.pageLoaded && this.revision?.preview_url === r.preview_url;
        this.revision = r;
        // W70: a record's linked-sheet card on this revision shows its point here instead of navigating
        this.unregisterFocus?.();
        this.unregisterFocus = this.sheetFocus.register(r.uuid, point => this.focusPoint(point));
        // W44: recently viewed sheets, per account
        if (r.sheet?.uuid) {
          this.recentSheets.record(this.projectId, { uuid: r.sheet.uuid, number: r.sheet.number, title: r.sheet.title });
        }
        this.findQuery = '';
        this.findMatches = [];
        this.findIndex = 0;
        this.findError = '';
        this.findBusy = false;
        this.findSeq++;
        if (this.findDoc && this.findDoc.file !== this.findFileKey(r)) {
          this.releaseFindDoc();
        }
        this.banner = r.status === 'superseded' ? 'superseded' : r.status === 'withdrawn' ? 'withdrawn' : null;
        this.redlining = false;
        this.editorConfig = this.buildConfig(r, true);
        this.layers = null;
        this.linked = [];
        this.linkedTotal = 0;
        this.selected = null;
        this.closeMark();
        this.drawerPin = null;
        // W54: the Filter (N) as the URL has it (the defaults when it has none)
        this.applyViewFilter(this.viewFilter);
        this.zoom = 1;
        this.cancelAddTo();
        this.placement = null;
        this.sheetLayers = [];
        this.carryCount = 0;
        this.carryFromLabel = null;
        // W64: another revision closes an open Edit sheet drawer
        this.cancelEditSheet();
        // Another revision: keep watching on a placed one, stop on one with no position on the ground
        if (!r.geo) {
          this.stopLocation();
        } else {
          this.placeFix();
        }
        this.loadLayers();
        this.loadCarry();
        this.loadLinked();
        this.loadPlacement();
        if (r.sheet && this.sheet?.uuid !== r.sheet.uuid) {
          this.api.getSheet(this.projectId, r.sheet.uuid).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
            next: s => (this.sheet = s),
            error: e => {
              this.forgetIfGone(e, r.sheet.uuid);
              this.fail(e);
            },
          });
        }
      },
      error: e => {
        this.stopLocation();
        this.fail(e);
      },
    });
  }

  private loadLayers(): void {
    const r = this.revision;
    if (!r) {
      return;
    }
    this.markup.getByAttachment(r.file.attachment_uuid, r.page_number).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: (rows: Markup[]) => {
        this.layers = rows;
        if (this.focusMarkUuid) {
          setTimeout(() => this.focusRequestedMark());
        }
        // An open card follows its mark's new state, and closes when the mark is gone.
        const open = this.selectedMark;
        if (open) {
          this.selectedMark = rows.find(l => l.id === open.id && isAsBuilt(l)) ?? null;
          if (!this.selectedMark) {
            this.closeMark();
          }
        }
      },
      error: e => this.fail(e),
    });
  }

  /** On the current revision, a publisher sees how many redlines from the previous one wait for review. */
  private loadCarry(): void {
    const r = this.revision;
    if (!r || !this.canPublish || r.status !== 'issued' || !r.supersedes_uuid || !r.sheet) {
      return;
    }
    this.api.carryForward(this.projectId, r.sheet.uuid).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: offer => {
        if (this.revision === r && offer?.to?.revision_uuid === r.uuid) {
          this.carryCount = offer.items?.length ?? 0;
          this.carryFromLabel = offer.from?.label ?? null;
        }
      },
      // Only a hint: the viewer works without it.
      error: () => (this.carryCount = 0),
    });
  }

  /**
   * Markers (one call, up to MARKER_CAP) and the first Linked here page. With `before`, a link that was not there
   * is a record just added from Add to, so it gets the toast.
   */
  private loadLinked(before?: Set<string>): void {
    const r = this.revision;
    if (!r) {
      return;
    }
    this.api.listLinked(this.projectId, r.uuid, { start: 0, total: MARKER_CAP }).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: page => {
        this.linked = page.data;
        this.linkedTotal = page.totalRecords;
        const added = before ? page.data.find(l => !before.has(l.uuid)) : null;
        if (added) {
          this.track.track('record_linked', { record_type: added.entity_type });
          this.selected = added;
          const noun = linkedRecordNoun(added.entity_type);
          this.notify.notification(`Added to ${noun === 'RFI' ? noun : noun.toLowerCase()} ${added.label ?? ''} · marker ${added.marker_index}`.trim(), 'success');
          this.loadLayers();
        }
      },
      error: e => this.fail(e),
    });
    this.loadPanel(0);
  }

  private loadPanel(start: number): void {
    const r = this.revision;
    if (!r) {
      return;
    }
    this.panelLoading = true;
    this.api.listLinked(this.projectId, r.uuid, { start, total: PANEL_PAGE }).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: page => {
        this.panelRows = page.data;
        this.panelDays = page.days;
        this.panelTotal = page.totalRecords;
        this.panelStart = start;
        this.panelLoading = false;
      },
      error: e => {
        this.panelLoading = false;
        this.fail(e);
      },
    });
  }

  openPlace(): void {
    const r = this.revision;
    if (!r || !this.mapPanels || !this.canPlace) {
      return;
    }
    const flagged = this.flaggedLayers;
    const ref = this.dialog.open(this.mapPanels.place, {
      data: {
        projectId: this.projectId,
        revision: r,
        // One flagged layer is the one to replace; with several the API asks and the dialog offers the list.
        supersedesLayerId: flagged.length === 1 ? flagged[0].id : null,
        flaggedLayers: flagged.map(l => ({ id: l.id, name: l.name })),
        // By stations fits the map to the sheet's stations on the alignment
        stations: this.sheet && this.sheet.uuid === r.sheet?.uuid ? { start: this.sheet.station_start, end: this.sheet.station_end } : null,
      },
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      autoFocus: false,
    });
    ref.afterClosed().pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe((result?: { versionUuid?: string; groupId?: string | null; groupName?: string }) => {
      if (result?.versionUuid) {
        this.openReview(result.versionUuid, result);
      } else {
        this.loadPlacement();
      }
    });
  }

  /**
   * A placement left waiting for review: reopen the review on this revision's own placement version (W38). Every
   * revision of a sheet places into the sheet's one source, so the source's latest version can be another revision's.
   */
  reviewPlacement(): void {
    const number = this.revision?.sheet?.number;
    if (!number) {
      return;
    }
    if (this.placement?.versionUuid) {
      this.openReview(this.placement.versionUuid, this.placement);
      return;
    }
    // An API without versionUuid: the source's latest is this revision's only when no other revision is in review
    this.otherRevisionInReview().pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe(other => {
      if (other) {
        this.notify.notification('The placement is in Settings › Map layers › Pending.', 'success');
      } else {
        this.reviewLatestPlacement(number);
      }
    });
  }

  /** Whether another revision of this sheet has a placement waiting for review (an unreadable one counts) */
  private otherRevisionInReview(): Observable<boolean> {
    const others = (this.sheet?.revisions ?? []).filter(r => r.uuid !== this.revision?.uuid);
    if (!others.length) {
      return of(false);
    }
    return forkJoin(others.map(r => this.api.placement(this.projectId, r.uuid).pipe(
      map(p => p.status === 'review'),
      catchError(() => of(true)),
    ))).pipe(map(inReview => inReview.some(Boolean)));
  }

  private reviewLatestPlacement(number: string): void {
    this.sources.list(this.projectId, { offset: 0, limit: 50, search: number }).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: page => {
        const source = page.sources.find(s => s.kind === 'placed_plan' && s.latestVersion?.status === 'ready_for_review'
          && (s.name === number || s.name.startsWith(`${number} · `)));
        if (source) {
          this.openReview(source.latestVersion.id, this.placement);
        } else {
          this.notify.notification('The placement is in Settings › Map layers › Pending.', 'success');
        }
      },
      error: e => this.fail(e),
    });
  }

  /** R50: drops the placement waiting for review, after a confirm; what is on the map now stays */
  discardPlacement(): void {
    const r = this.revision;
    if (!r || !this.canDiscardPlacement) {
      return;
    }
    const name = `${r.sheet?.number ?? 'this sheet'}${r.label ? ` Rev ${r.label}` : ''}`;
    const model = new ConfirmDialogModel(
      'Discard placement',
      `The placement of ${name} waiting for review will be discarded. What is on the map now stays as it is, and you can place the sheet again.`,
      'Discard',
      'Cancel',
      true,
    );
    this.dialog.open(ConfirmDialogComponent, { data: model }).afterClosed().pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe(ok => {
      if (!ok) {
        return;
      }
      this.api.discardPlacement(this.projectId, r.uuid).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
        next: () => {
          this.notify.notification('Placement discarded', 'success');
          // The staged layer may be gone from Settings › Map layers › Pending too
          this.sources.listChanged.next();
          this.loadPlacement();
        },
        error: e => {
          this.fail(e);
          this.loadPlacement();
        },
      });
    });
  }

  /**
   * Withdraw the sheet's current revision (Sri; the register's confirm, drawing-withdraw.ts). A placed one keeps its map
   * layer, which the confirm says. Then the sheet is read again so the header, History and ⋯ follow; with no revision
   * left there is nothing to show, so back to the register.
   */
  withdrawCurrent(): void {
    const s = this.sheet;
    if (!s?.current || !this.canWithdrawCurrent) {
      return;
    }
    const current = s.current;
    const placed = this.sheetLayers.some(l => l.drawing?.revisionUuid === current.uuid)
      || (this.revision?.uuid === current.uuid && this.placement?.status === 'published');
    confirmAndWithdraw(this.dialog, this.api, this.projectId, { revisionUuid: current.uuid, number: s.number, label: current.label, placed })
      .pipe(
        switchMap(() => this.api.getSheet(this.projectId, s.uuid)),
        takeUntil(merge(this.reset, this.onDestroy)),
      )
      .subscribe({
        next: fresh => {
          this.notify.notification(`Withdrew ${s.number} Rev ${current.label}`, 'success');
          if (!fresh.current) {
            this.back.emit();
            return;
          }
          this.sheet = fresh;
          if (this.revision) {
            this.loadRevision(this.revision.uuid);
          }
        },
        error: e => this.fail(e),
      });
  }

  /**
   * Remove from map (Sri, api 3692cccc): after a confirm, unpublishes the sheet's placed layer. Records pinned on the sheet
   * keep their sheet location. The project map and Settings › Map layers refresh, and ⋯ offers Place on map again.
   */
  removeFromMap(): void {
    const r = this.revision;
    if (!r || !this.canRemoveFromMap) {
      return;
    }
    const name = `${r.sheet?.number ?? 'this sheet'}${r.label ? ` Rev ${r.label}` : ''}`;
    const model = new ConfirmDialogModel(
      'Remove from map',
      `Remove ${name} from the map? Records pinned on this sheet keep their sheet location.`,
      'Remove',
      'Cancel',
      true,
    );
    this.dialog.open(ConfirmDialogComponent, { data: model }).afterClosed().pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe(ok => {
      if (!ok) {
        return;
      }
      this.api.removePlacement(this.projectId, r.uuid, this.catalogRevision).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
        next: () => {
          this.notify.notification('Removed from the map', 'success');
          this.refLayers.listChanged.next();
          this.sources.listChanged.next();
          this.loadPlacement();
        },
        error: e => {
          // In review, the map changed, or already off it: say so and show the state as it now is
          this.track.error('viewer', e);
          this.notify.notification(this.api.removeFailure(e), 'error');
          this.loadPlacement();
        },
      });
    });
  }

  // W44 Find on sheet ------------------------------------------------------------------------------------------------

  toggleFind(): void {
    this.findOpen = !this.findOpen;
    if (!this.findOpen) {
      this.findQuery = '';
      this.findMatches = [];
      this.findError = '';
      return;
    }
    this.cdr.detectChanges();
    (this.host?.nativeElement?.querySelector?.('[data-testid="drawing-viewer-find-input"]') as HTMLInputElement | null)?.focus();
  }

  /** Searches the page's text layer (the set PDF through pdf.js, only this page's ranges) and highlights each match */
  async runFind(query: string): Promise<void> {
    this.findQuery = query;
    const r = this.revision;
    if (!r || !query.trim()) {
      this.findMatches = [];
      this.findIndex = 0;
      this.findError = '';
      return;
    }
    const seq = ++this.findSeq;
    this.findBusy = true;
    this.findError = '';
    this.cdr.markForCheck();
    try {
      const text = await this.loadPageText(r);
      if (seq !== this.findSeq) {
        return;
      }
      this.findMatches = findTextBoxes(text.items, text.viewport, query);
      this.track.track('sheet_text_find_used', { matches: this.findMatches.length });
      this.findIndex = 0;
      if (!this.findMatches.length) {
        this.findError = text.items.length ? 'No match on this sheet.' : 'This sheet has no searchable text (a scan).';
      }
      this.scrollToFind();
    } catch {
      if (seq === this.findSeq) {
        this.findMatches = [];
        this.findError = 'The sheet text could not be read. Try again.';
      }
    } finally {
      if (seq === this.findSeq) {
        this.findBusy = false;
        this.cdr.markForCheck();
      }
    }
  }

  findStep(step: number): void {
    const n = this.findMatches.length;
    if (!n) {
      return;
    }
    this.findIndex = (this.findIndex + step + n) % n;
    this.scrollToFind();
  }

  /** The page's text items, read once per revision */
  protected async loadPageText(r: { uuid: string; page_number: number; file?: { attachment_uuid?: string | null; original_url?: string | null; page_number?: number } }):
    Promise<{ items: PdfTextItem[]; viewport: PdfViewportLike }> {
    if (this.pageText?.uuid === r.uuid) {
      return this.pageText;
    }
    const url = r.file?.original_url;
    if (!url) {
      throw new Error('No PDF');
    }
    const file = this.findFileKey(r);
    if (this.findDoc?.file !== file) {
      this.releaseFindDoc();
      this.findDoc = { file, task: this.pdfRender.openDocument(url, PDF_PAGE_RANGES) };
    }
    const doc = await this.findDoc.task.promise;
    const page = await doc.getPage(r.file?.page_number || r.page_number || 1);
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    const items = (content.items as unknown[]).filter((i): i is PdfTextItem => typeof (i as PdfTextItem)?.str === 'string');
    this.pageText = { uuid: r.uuid, items, viewport: viewport as unknown as PdfViewportLike };
    return this.pageText;
  }

  private findFileKey(r: { file?: { attachment_uuid?: string | null; original_url?: string | null } }): string {
    return r.file?.attachment_uuid || r.file?.original_url || '';
  }

  /** Frees the Find PDF (and cancels its load if still running) */
  private releaseFindDoc(): void {
    const task = this.findDoc?.task;
    this.findDoc = null;
    this.pageText = null;
    task?.destroy().catch(() => undefined);
  }

  /** A sheet that is gone or out of reach leaves Recent (W44 I2) */
  private forgetIfGone(e: { status?: number } | null, sheetUuid: string | null | undefined): void {
    if (sheetUuid && (e?.status === 404 || e?.status === 403)) {
      this.recentSheets.forget(this.projectId, sheetUuid);
    }
  }

  /** Brings the match in focus into view inside the page frame */
  private scrollToFind(): void {
    this.cdr.detectChanges();
    const hit = this.host?.nativeElement?.querySelector?.('.find-hit--current') as HTMLElement | null;
    hit?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
  }

  /**
   * Sri: the project map sits above the page, usually scrolled out of view, so panning it looked like nothing happened.
   * Open it full screen (the map's own ⤢, ?fs), then ask it to turn the sheet's layer on and fit it once it has resized.
   * Not tied to this view's lifetime: the map outlives it.
   */
  showOnMap(): void {
    const layer = this.mapLayer;
    if (!layer) {
      return;
    }
    void this.router.navigate([], { queryParams: { fs: 1 }, queryParamsHandling: 'merge' }).then(() => {
      setTimeout(() => this.projectsService.showMapLayer.next(layer.id), SHOW_ON_MAP_SETTLE_MS);
    });
  }

  /** W56: Expand, and ✕ / Esc / browser back to leave; the wrapper keeps it in the URL, so the input follows */
  toggleFullScreen(on: boolean, source: 'button' | 'click' | 'keyboard' | 'escape' = 'button'): void {
    if (on === this.fullScreen) {
      return;
    }
    this.track.track('sheet_fullscreen_toggled', { on, source });
    this.fullScreenChange.emit(on);
  }

  toggleDetails(): void {
    this.detailsOpen = !this.detailsOpen;
    // The panel covers the right of the frame: the fit makes room for it (and takes it back)
    setTimeout(() => this.refitFull());
  }

  /** Esc leaves full screen once nothing else wants it: a field, a menu or popup, the mark card, a drawer, Find or Add to */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('input, textarea, select, .cdk-overlay-container') || document.querySelector('.cdk-overlay-backdrop')) {
      return;
    }
    if (this.drawerOpen || this.findOpen || this.selectedMark || this.redlining) {
      return;
    }
    // W60: Esc cancels placing, docked or full screen
    if (this.placing) {
      event.preventDefault();
      this.cancelAddTo();
      return;
    }
    if (!this.fullScreen) {
      return;
    }
    if (this.selected) {
      this.closePeek();
      return;
    }
    event.preventDefault();
    this.toggleFullScreen(false, 'escape');
  }

  /** The overlay in or out: the app header hides as for the photo overlay; the zoom stays and the same page point stays centred */
  private applyFullScreen(): void {
    const frame = this.pageFrame?.nativeElement;
    const centre = frame && frame.scrollWidth > 0
      ? { x: (frame.scrollLeft + frame.clientWidth / 2) / frame.scrollWidth, y: (frame.scrollTop + frame.clientHeight / 2) / frame.scrollHeight }
      : null;
    if (!this.fullScreen) {
      this.detailsOpen = false;
      // W61: back to the docked page at the same zoom relative to its fit
      this.fullFit = null;
      this.watchFullResize(false);
    }
    this.setFullScreenChrome(this.fullScreen);
    const entering = this.fullScreen;
    setTimeout(() => {
      if (entering) {
        // W61: 1× fits the frame now laid out full screen; the zoom stays the same relative to the fit
        this.refitFull();
        this.watchFullResize(true);
      } else if (this.zoom > 1) {
        // W61: and back to the docked page's 1×, measured on the docked frame now laid out
        this.baseWidth = this.dockedBase();
      }
      this.cdr.detectChanges();
      const f = this.pageFrame?.nativeElement;
      if (f && centre && (this.zoom > 1 || entering)) {
        f.scrollLeft = centre.x * f.scrollWidth - f.clientWidth / 2;
        f.scrollTop = centre.y * f.scrollHeight - f.clientHeight / 2;
      }
      // Review M3: focus moves into the overlay (its toolbar's first control) and back to Expand on the way out
      const host = this.host.nativeElement;
      const target = entering
        ? host.querySelector<HTMLElement>('.viewer-body--full .stage-tools button:not([disabled])')
        : host.querySelector<HTMLElement>('[data-testid="drawing-viewer-fullscreen"]');
      target?.focus();
    });
  }

  /** W61: the window or the banners above the frame change its size: refit */
  private watchFullResize(on: boolean): void {
    this.fullResize?.disconnect();
    this.fullResize = null;
    const frame = this.pageFrame?.nativeElement;
    if (on && frame && typeof ResizeObserver !== 'undefined') {
      this.fullResize = new ResizeObserver(() => this.refitFull());
      this.fullResize.observe(frame);
    }
  }

  private setFullScreenChrome(on: boolean): void {
    const body = typeof document !== 'undefined' ? document.body : null;
    body?.classList.toggle('picture-overlay-open', on);
    body?.classList.toggle('drawing-sheet-full', on);
    this.setBackgroundInert(on);
  }

  /**
   * Review M3: while full, the page behind the overlay is inert (no Tab, no screen reader), sibling by sibling up to
   * <body>. Not the right drawer ([data-drawings-drawer]; Add to slides over the overlay) nor the CDK container (menus,
   * dialogs): those stay live.
   */
  private setBackgroundInert(on: boolean): void {
    this.inerted.forEach(el => el.removeAttribute('inert'));
    this.inerted = [];
    const overlay = this.host.nativeElement.querySelector<HTMLElement>('[data-testid="drawing-viewer-body"]');
    if (!on || !overlay) {
      return;
    }
    // The project right drawer is marked data-drawings-drawer (projects-wrapper); not any element that happens to be .right (review N1)
    const keep = (el: Element) =>
      el.matches('.cdk-overlay-container, [data-drawings-drawer], script, style') || !!el.querySelector('.cdk-overlay-container, [data-drawings-drawer]');
    for (let node: HTMLElement | null = overlay; node && node !== document.body; node = node.parentElement) {
      for (const sibling of Array.from(node.parentElement?.children ?? [])) {
        if (sibling !== node && !keep(sibling) && !sibling.hasAttribute('inert')) {
          sibling.setAttribute('inert', '');
          this.inerted.push(sibling as HTMLElement);
        }
      }
    }
  }

  /** The pending pin scrolled into view in the sheet frame (the drawer covers the right of the window) */
  /**
   * W70: a record's card asked for its point on this sheet: scroll it into view (the pending pin when the record is
   * being created) and pulse it briefly.
   */
  focusPoint(point: { x: number; y: number } | null): void {
    const at = this.drawerPin ?? (point ? { x: point.x, y: point.y } : null);
    if (!at) {
      return;
    }
    this.focusPulse = at;
    this.cdr.detectChanges();
    const ring = this.host?.nativeElement?.querySelector?.('[data-testid="drawing-viewer-focus-pulse"]') as HTMLElement | null;
    ring?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
    if (this.focusPulseTimer) {
      clearTimeout(this.focusPulseTimer);
    }
    this.focusPulseTimer = setTimeout(() => {
      this.focusPulse = null;
      this.focusPulseTimer = null;
      this.cdr.markForCheck();
    }, FOCUS_PULSE_MS);
  }

  private revealDrawerPin(): void {
    const pin = this.host?.nativeElement?.querySelector?.('[data-testid="drawing-viewer-drawer-pin"]') as HTMLElement | null;
    pin?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
  }

  /** Runs `then` when the right panel this viewer just opened closes; replaces any earlier pending listener. */
  private onPanelClosed(then: () => void): void {
    this.panelClosed?.unsubscribe();
    this.panelClosed = this.projectsService.closeRightPanel.pipe(take(1), takeUntil(merge(this.reset, this.onDestroy))).subscribe(() => {
      this.panelClosed = null;
      then();
    });
  }

  /** The map layer review publishes the placed sheet into the API's Drawings group (T16, W22). */
  private openReview(versionUuid: string, group: { groupId?: string | null; groupName?: string } | null): void {
    const r = this.revision;
    if (!r || !this.mapPanels) {
      return;
    }
    this.onPanelClosed(() => this.loadPlacement());
    this.projectsService.openRightPanel.next({
      component: this.mapPanels.layer,
      data: {
        projectId: this.projectId,
        sourceVersionId: versionUuid,
        name: `${r.sheet?.number ?? 'Sheet'} Rev ${r.label ?? ''}`.trim(),
        groupId: group?.groupId ?? null,
        groupName: group?.groupName || DRAWINGS_GROUP,
      },
    });
  }

  /** The placement and the catalog layers of this sheet. No catalog (404) or no map rights just means nothing is placed. */
  private loadPlacement(): void {
    const r = this.revision;
    if (!r) {
      return;
    }
    this.api.placement(this.projectId, r.uuid).pipe(takeUntil(merge(this.reset, this.onDestroy))).subscribe({
      next: p => (this.placement = p),
      error: () => (this.placement = null),
    });
    const sheetUuid = r.sheet?.uuid;
    const projectId = this.projectId;
    this.manifestApi.manifest(projectId).then(
      ({ manifest }) => {
        if (this.revision === r && this.projectId === projectId) {
          this.sheetLayers = (manifest?.layers ?? []).filter(l => !!sheetUuid && l.drawing?.sheetUuid === sheetUuid);
          this.catalogRevision = manifest?.catalogRevision ?? null;
          this.cdr.markForCheck();
        }
      },
      () => {
        if (this.revision === r) {
          this.sheetLayers = [];
        }
      },
    );
  }

  /** Read-only uses the page image alone; Redline adds the set PDF and the row to edit. */
  private buildConfig(r: DrawingRevisionDetail, readOnly: boolean): MarkupEditorConfig {
    const config: MarkupEditorConfig = {
      imageUrl: r.preview_url,
      attachmentId: r.file.attachment_uuid,
      pageNumber: r.page_number,
      isDocument: true,
      readOnly,
      filename: `${r.sheet?.number ?? 'Sheet'} Rev ${r.label ?? ''}`.trim(),
    };
    if (!readOnly) {
      config.originalUrl = r.file.original_url;
      config.visibility = 'shared';
    }
    return config;
  }

  /** W72: streams this revision's PDF in the chosen mode; the menu item stays busy until the file is handed over. */
  downloadSheet(mode: DrawingDownloadMode): void {
    const r = this.revision;
    if (!r || this.downloadingMode) {
      return;
    }
    this.downloadingMode = mode;
    this.track.track('sheet_downloaded', { mode });
    const suffix = mode === 'redlines' ? ' with redlines' : mode === 'as_built' ? ' with as-built marks' : '';
    const fallback = `${r.sheet?.number ?? 'Sheet'} Rev ${r.label ?? ''}${suffix}.pdf`;
    this.api.downloadRevision(this.projectId, r.uuid, mode, fallback).pipe(takeUntil(this.onDestroy)).subscribe({
      next: () => { this.downloadingMode = null; this.cdr.markForCheck(); },
      error: (e) => { this.downloadingMode = null; this.cdr.markForCheck(); this.fail(e); },
    });
  }

  private fail(e: { error?: { code?: string } } | null): void {
    this.track.error('viewer', e);
    this.notify.notification(this.api.failureMessage(e?.error?.code, this.api.errorMessage(e)), 'error');
  }
}
